import * as echarts from "echarts";
import { createIcons, icons } from "lucide";
import type { BossAgentAnswer, OpsTask, SessionUser, TaskKind, TaskRiskLevel } from "@boss-agent/contracts";
import {
  ApiError,
  askBossAgent,
  callApi,
  changePassword,
  createPairingCode,
  createTask,
  currentApiToken,
  currentCsrfToken,
  generatePromotionPlans,
  getDashboard,
  getPeriodReport,
  getSession,
  getTask,
  listContentAssets,
  listDevices,
  listMetricRecords,
  listPromotionPolicies,
  listTakeoutAccounts,
  listTakeoutIntegrations,
  listTakeoutPlans,
  listTasks,
  login,
  registerContentAsset,
  logout,
  revokeDevice,
  savePromotionPolicy,
  saveTakeoutAccount,
  transitionTask,
  updateTakeoutPlanStatus,
  type DashboardData,
  type ContentAsset,
  type DeviceInfo,
  type PairingCode,
  type PromotionPolicy,
  type MetricRecord,
  type TakeoutAccount,
  type TakeoutIntegration,
  type TakeoutPlan,
  type TakeoutPlatformId
} from "./api";
import "./styles.css";

type PageId =
  | "dashboard"
  | "agent"
  | "stores"
  | "operations"
  | "data"
  | "content"
  | "media"
  | "tasks"
  | "devices"
  | "subscription"
  | "settings";

const PAGE_META: Record<PageId, [string, string]> = {
  dashboard: ["老板驾驶舱", "全组织经营态势、风险和待办"],
  agent: ["老板智能体", "基于真实经营数据生成诊断和任务建议"],
  stores: ["门店与组织", "统一门店、区域、负责人和平台绑定"],
  operations: ["三平台运营", "推广、装修、菜单和评价计划"],
  data: ["经营数据", "手工录入与 CSV/XLSX 导入"],
  content: ["智能运营", "外卖、团购、小红书与短视频内容"],
  media: ["媒体中心", "图片和 FFmpeg 视频任务"],
  tasks: ["任务与审批", "云端审批后由 Mac 执行端安全执行"],
  devices: ["执行端管理", "配对、查看和撤销 Mac 执行设备"],
  subscription: ["订阅与点数", "当前套餐、AI 点数和激活码"],
  settings: ["系统设置", "账号安全与产品状态"]
};

const NAV_ITEMS: Array<{ id: PageId; label: string; icon: string; group?: string }> = [
  { id: "dashboard", label: "老板驾驶舱", icon: "layout-dashboard", group: "总览" },
  { id: "agent", label: "老板智能体", icon: "sparkles", group: "总览" },
  { id: "stores", label: "门店与组织", icon: "store", group: "经营" },
  { id: "operations", label: "三平台运营", icon: "chart-no-axes-combined", group: "经营" },
  { id: "data", label: "经营数据", icon: "database-zap", group: "经营" },
  { id: "content", label: "智能运营", icon: "wand-sparkles", group: "增长" },
  { id: "media", label: "媒体中心", icon: "image-play", group: "增长" },
  { id: "tasks", label: "任务与审批", icon: "clipboard-check", group: "执行" },
  { id: "devices", label: "执行端管理", icon: "monitor-cog", group: "执行" },
  { id: "subscription", label: "订阅与点数", icon: "badge-check", group: "账号" },
  { id: "settings", label: "系统设置", icon: "settings", group: "账号" }
];

interface AppState {
  user: SessionUser | null;
  page: PageId;
  dashboard: DashboardData | null;
  metrics: MetricRecord[];
  tasks: OpsTask[];
  devices: DeviceInfo[];
  pairingCodes: PairingCode[];
  assets: ContentAsset[];
  integrations: TakeoutIntegration[];
  takeoutAccounts: TakeoutAccount[];
  takeoutPlans: TakeoutPlan[];
  promotionPolicies: PromotionPolicy[];
  agentAnswer: BossAgentAnswer | null;
  periodReport: string | null;
  platformTask: OpsTask | null;
  platformToolBusy: "" | "open" | "collect" | "promotion";
  platformPollTaskId: string;
  chart: echarts.ECharts | null;
}

const state: AppState = {
  user: null,
  page: "dashboard",
  dashboard: null,
  metrics: [],
  tasks: [],
  devices: [],
  pairingCodes: [],
  assets: [],
  integrations: [],
  takeoutAccounts: [],
  takeoutPlans: [],
  promotionPolicies: [],
  agentAnswer: null,
  periodReport: null,
  platformTask: null,
  platformToolBusy: "",
  platformPollTaskId: "",
  chart: null
};

const appElement = document.querySelector<HTMLDivElement>("#app");
if (!appElement) throw new Error("缺少应用挂载节点");
const app: HTMLDivElement = appElement;

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function money(value: number): string {
  return `¥${Number(value || 0).toLocaleString("zh-CN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
}

function number(value: number): string {
  return Number(value || 0).toLocaleString("zh-CN");
}

function relativeTime(value?: string): string {
  if (!value) return "从未";
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds} 秒前`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟前`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} 小时前`;
  return `${Math.floor(seconds / 86400)} 天前`;
}

function statusLabel(status: string): string {
  return {
    draft: "草稿",
    pending_approval: "待审批",
    approved: "已批准",
    queued: "待领取",
    claimed: "已领取",
    running: "执行中",
    local_confirmation_required: "待本机确认",
    succeeded: "已完成",
    failed: "失败",
    cancelled: "已取消",
    online: "在线",
    offline: "离线",
    revoked: "已撤销"
  }[status] || status;
}

function toast(message: string, type: "success" | "error" | "warning" = "success"): void {
  const element = document.querySelector<HTMLDivElement>("#toast");
  if (!element) return;
  element.textContent = message;
  element.className = `toast visible ${type}`;
  window.setTimeout(() => {
    element.className = "toast";
  }, 3200);
}

function icon(name: string): string {
  return `<i data-lucide="${escapeHtml(name)}" aria-hidden="true"></i>`;
}

function renderMarkdown(source: string): string {
  const lines = String(source || "").split("\n");
  const html: string[] = [];
  let inTable = false;
  let inList = false;
  const inline = (text: string): string => {
    let value = escapeHtml(text);
    value = value.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    value = value.replace(/`(.+?)`/g, "<code>$1</code>");
    return value;
  };
  const closeList = () => {
    if (inList) {
      html.push("</ul>");
      inList = false;
    }
  };
  const closeTable = () => {
    if (inTable) {
      html.push("</tbody></table>");
      inTable = false;
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (/^\|.*\|$/.test(line)) {
      if (!inTable) {
        closeList();
        html.push('<table class="report-table"><tbody>');
        inTable = true;
      }
      const cells = line.replace(/^\||\|$/g, "").split("|").map((cell) => cell.trim());
      if (/^:?-+:?$/.test(cells[0] || "")) continue;
      html.push(`<tr>${cells.map((cell) => `<td>${inline(cell)}</td>`).join("")}</tr>`);
      continue;
    }
    closeTable();
    if (line.startsWith("### ")) {
      closeList();
      html.push(`<h3>${inline(line.slice(4))}</h3>`);
    } else if (line.startsWith("## ")) {
      closeList();
      html.push(`<h2>${inline(line.slice(3))}</h2>`);
    } else if (/^[-*] /.test(line)) {
      if (!inList) {
        html.push("<ul>");
        inList = true;
      }
      html.push(`<li>${inline(line.slice(2))}</li>`);
    } else if (line) {
      closeList();
      html.push(`<p>${inline(line)}</p>`);
    }
  }
  closeTable();
  closeList();
  return html.join("");
}

function refreshIcons(): void {
  createIcons({
    icons,
    attrs: {
      "stroke-width": 1.8
    }
  });
}

function renderLogin(message = ""): void {
  app.innerHTML = `
    <main class="login-page">
      <section class="login-brand">
        <div class="brand-mark">侠客</div>
        <p class="eyebrow">AL BAOZHANG × WAIMAI XIAKE</p>
        <h1>外卖侠客<br />老板智能体</h1>
        <p class="login-intro">经营数据、三平台运营、任务审批和 Mac 执行端统一到一个老板视角。</p>
        <div class="login-points">
          <span>${icon("shield-check")} 高风险动作双重确认</span>
          <span>${icon("monitor-check")} 平台密码不离开执行端</span>
          <span>${icon("brain-circuit")} 云端 AI 与本机 Codex 协同</span>
        </div>
      </section>
      <section class="login-panel">
        <form class="login-card" id="login-form">
          <div>
            <p class="eyebrow">统一账号登录</p>
            <h2>进入老板驾驶舱</h2>
          </div>
          <label>
            <span>用户名</span>
            <input name="username" autocomplete="username" required autofocus />
          </label>
          <label>
            <span>密码</span>
            <input name="password" type="password" autocomplete="current-password" required />
          </label>
          <div class="form-error" id="login-error">${escapeHtml(message)}</div>
          <button class="primary-button wide" type="submit">${icon("log-in")} 登录</button>
          <p class="login-hint">原外卖侠客账号可直接迁移，首次登录后需要修改密码。</p>
        </form>
      </section>
    </main>
  `;
  refreshIcons();
  document.querySelector<HTMLFormElement>("#login-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const form = new FormData(formElement);
    const submit = formElement.querySelector<HTMLButtonElement>("button[type=submit]");
    if (submit) submit.disabled = true;
    try {
      state.user = await login(String(form.get("username") || ""), String(form.get("password") || ""));
      await loadWorkspace();
    } catch (error) {
      renderLogin(error instanceof Error ? error.message : "登录失败");
    }
  });
}

function renderShell(): void {
  if (!state.user) return renderLogin();
  const groups = [...new Set(NAV_ITEMS.map((item) => item.group))];
  app.innerHTML = `
    <div class="app-shell">
      <aside class="sidebar" id="sidebar">
        <div class="brand">
          <div class="brand-mark">侠客</div>
          <div>
            <strong>老板智能体</strong>
            <span>统一经营控制台</span>
          </div>
        </div>
        <nav class="nav-list" aria-label="主导航">
          ${groups
            .map(
              (group) => `
                <p class="nav-group">${escapeHtml(group)}</p>
                ${NAV_ITEMS.filter((item) => item.group === group)
                  .map(
                    (item) => `
                      <button class="nav-item ${state.page === item.id ? "active" : ""}" data-page="${item.id}">
                        ${icon(item.icon)}<span>${escapeHtml(item.label)}</span>
                      </button>
                    `
                  )
                  .join("")}
              `
            )
            .join("")}
        </nav>
        <div class="sidebar-foot">
          <div class="plan-badge">
            <span>${escapeHtml(state.user.organization.planName)}</span>
            <small>${number(state.user.organization.aiPointsBalance)} AI 点数</small>
          </div>
          <button class="ghost-button" id="logout-button">${icon("log-out")} 退出</button>
        </div>
      </aside>
      <main class="main">
        <header class="topbar">
          <button class="mobile-menu" id="mobile-menu" aria-label="打开导航">${icon("menu")}</button>
          <div>
            <p class="eyebrow">${escapeHtml(state.user.organization.name)}</p>
            <h1 id="page-title">${escapeHtml(PAGE_META[state.page][0])}</h1>
            <p id="page-subtitle">${escapeHtml(PAGE_META[state.page][1])}</p>
          </div>
          <div class="top-actions">
            <button class="secondary-button" data-page="agent">${icon("sparkles")} 问老板智能体</button>
            <div class="user-chip">
              <span>${escapeHtml(state.user.displayName.slice(0, 1))}</span>
              <div><strong>${escapeHtml(state.user.displayName)}</strong><small>${escapeHtml(state.user.role)}</small></div>
            </div>
          </div>
        </header>
        <section class="page-content" id="page-content"></section>
      </main>
      <nav class="mobile-nav">
        ${[
          ["dashboard", "总览", "layout-dashboard"],
          ["agent", "智能体", "sparkles"],
          ["tasks", "任务", "clipboard-check"],
          ["devices", "执行端", "monitor-cog"],
          ["settings", "我的", "user-round-cog"]
        ]
          .map(
            ([id, label, iconName]) => `
              <button class="${state.page === id ? "active" : ""}" data-page="${id}">
                ${icon(String(iconName))}<span>${escapeHtml(label)}</span>
              </button>
            `
          )
          .join("")}
      </nav>
    </div>
  `;
  wireNavigationButtons();
  document.querySelector("#logout-button")?.addEventListener("click", async () => {
    await logout();
    state.user = null;
    renderLogin();
  });
  document.querySelector("#mobile-menu")?.addEventListener("click", () => {
    document.querySelector("#sidebar")?.classList.toggle("open");
  });
  renderPage();
  refreshIcons();
}

function wireNavigationButtons(): void {
  document.querySelectorAll<HTMLElement>("[data-page]").forEach((button) => {
    if (button.dataset.navigationBound === "true") return;
    button.dataset.navigationBound = "true";
    button.addEventListener("click", () => navigate(button.dataset.page as PageId));
  });
}

function navigate(page: PageId): void {
  if (!PAGE_META[page]) return;
  state.page = page;
  document.querySelectorAll(".nav-item, .mobile-nav button").forEach((element) => {
    element.classList.toggle("active", (element as HTMLElement).dataset.page === page);
  });
  const title = document.querySelector("#page-title");
  const subtitle = document.querySelector("#page-subtitle");
  if (title) title.textContent = PAGE_META[page][0];
  if (subtitle) subtitle.textContent = PAGE_META[page][1];
  document.querySelector("#sidebar")?.classList.remove("open");
  renderPage();
  refreshIcons();
}

function localDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

type PlatformBucket = "eleme" | "meituan" | "jd";

interface DayStoreMetric {
  eleme: { orders: number; revenue: number };
  meituan: { orders: number; revenue: number };
  jd: { orders: number; revenue: number };
  total: { orders: number; revenue: number };
}

function dayMetricsByStore(date: string): Map<string, DayStoreMetric> {
  const map = new Map<string, DayStoreMetric>();
  for (const metric of state.metrics) {
    if (metric.metricDate !== date) continue;
    let entry = map.get(metric.storeId);
    if (!entry) {
      entry = {
        eleme: { orders: 0, revenue: 0 },
        meituan: { orders: 0, revenue: 0 },
        jd: { orders: 0, revenue: 0 },
        total: { orders: 0, revenue: 0 }
      };
      map.set(metric.storeId, entry);
    }
    const bucket: PlatformBucket | null =
      metric.channel === "eleme_takeout"
        ? "eleme"
        : metric.channel === "meituan_takeout"
          ? "meituan"
          : metric.channel === "jd_takeout"
            ? "jd"
            : null;
    if (!bucket) continue;
    entry[bucket].orders += Number(metric.orders || 0);
    entry[bucket].revenue += Number(metric.revenue || 0);
    entry.total.orders += Number(metric.orders || 0);
    entry.total.revenue += Number(metric.revenue || 0);
  }
  return map;
}

function platformMetricCell(metric?: { orders: number; revenue: number }): string {
  if (!metric || (metric.orders <= 0 && metric.revenue <= 0)) {
    return '<span class="muted">--</span>';
  }
  return `<strong>${number(metric.orders)} 单</strong><small>${money(metric.revenue)}</small>`;
}

function storeDayRow(
  store: DashboardData["stores"][number],
  day: DayStoreMetric | undefined,
  rank?: number
): string {
  const total = day?.total || { orders: 0, revenue: 0 };
  const rankBadge = rank
    ? `<span class="rank-badge ${rank <= 3 ? `rank-${rank}` : "rank-plain"}">${rank}</span>`
    : "";
  return `
    <tr>
      <td><span class="store-cell">${rankBadge}<span><strong>${escapeHtml(store.name)}</strong><small>${escapeHtml(store.city)} · ${escapeHtml(store.owner || "未指定负责人")}</small></span></span></td>
      <td>${platformMetricCell(day?.eleme)}</td>
      <td>${platformMetricCell(day?.meituan)}</td>
      <td>${platformMetricCell(day?.jd)}</td>
      <td>${platformMetricCell(total)}</td>
    </tr>
  `;
}

function dailyGrowthInsight(
  stores: DashboardData["stores"],
  todayMap: Map<string, DayStoreMetric>,
  yesterdayMap: Map<string, DayStoreMetric>,
  pendingCount: number
): { headline: string; body: string; tags: string[] } {
  const byTodayOrders = [...stores]
    .filter((store) => (todayMap.get(store.id)?.total.orders || 0) > 0)
    .sort((a, b) => (todayMap.get(b.id)?.total.orders || 0) - (todayMap.get(a.id)?.total.orders || 0));
  const byYesterdayRevenue = [...stores]
    .filter((store) => (yesterdayMap.get(store.id)?.total.revenue || 0) > 0)
    .sort((a, b) => (yesterdayMap.get(b.id)?.total.revenue || 0) - (yesterdayMap.get(a.id)?.total.revenue || 0));
  const topToday = byTodayOrders[0];
  const topYesterday = byYesterdayRevenue[0];
  const totalTodayOrders = [...todayMap.values()].reduce((sum, item) => sum + item.total.orders, 0);
  const totalYesterdayOrders = [...yesterdayMap.values()].reduce((sum, item) => sum + item.total.orders, 0);

  if (!topToday && !topYesterday) {
    return {
      headline: "等待数据回传",
      body: "今日逐店数据尚未入库。建议先执行一键采集，或待平台日结后导入今日日报，再生成增长建议。",
      tags: ["待采集", "日报导入"]
    };
  }

  const tags: string[] = [];
  if (totalTodayOrders >= totalYesterdayOrders && totalYesterdayOrders > 0) {
    tags.push("订单同比较好");
  } else if (totalYesterdayOrders > 0) {
    tags.push("今日订单待追赶");
  }
  if (pendingCount > 0) tags.push(`${pendingCount} 项待处理`);

  const parts: string[] = [];
  if (topToday) {
    parts.push(`${topToday.name} 今日订单领跑（${number(todayMap.get(topToday.id)?.total.orders || 0)} 单）`);
  }
  if (topYesterday) {
    parts.push(`${topYesterday.name} 昨日营收最高（${money(yesterdayMap.get(topYesterday.id)?.total.revenue || 0)}）`);
  }
  parts.push(pendingCount > 0 ? "优先处理待审批事项，避免影响执行节奏。" : "建议复核首屏套餐与高峰出餐时长，抓住转化机会。");

  return {
    headline: "AI 店长建议",
    body: parts.join("；") + "。",
    tags
  };
}

function dashboardTemplate(): string {
  const dashboard = state.dashboard;
  if (!dashboard) return loadingTemplate("正在加载经营数据");

  const now = new Date();
  const today = localDateString(now);
  const yesterday = localDateString(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  const todayMap = dayMetricsByStore(today);
  const yesterdayMap = dayMetricsByStore(yesterday);

  const sumDay = (map: Map<string, DayStoreMetric>) =>
    [...map.values()].reduce(
      (total, item) => ({
        orders: total.orders + item.total.orders,
        revenue: total.revenue + item.total.revenue
      }),
      { orders: 0, revenue: 0 }
    );
  const todayTotal = sumDay(todayMap);
  const yesterdayTotal = sumDay(yesterdayMap);

  const stores = dashboard.stores || [];
  const todayStores = [...stores].sort(
    (a, b) =>
      (todayMap.get(b.id)?.total.orders || 0) - (todayMap.get(a.id)?.total.orders || 0) ||
      a.name.localeCompare(b.name, "zh-CN")
  );
  const yesterdayStores = [...stores].sort(
    (a, b) =>
      (yesterdayMap.get(b.id)?.total.revenue || 0) - (yesterdayMap.get(a.id)?.total.revenue || 0) ||
      a.name.localeCompare(b.name, "zh-CN")
  );

  const pendingTasks = (dashboard.recentTasks || []).filter((task) =>
    ["pending_approval", "claimed", "running", "local_confirmation_required"].includes(task.status)
  );

  const pendingIssues = [...dashboard.alerts, ...pendingTasks.map((task) => ({
    id: `pending-${task.id}`,
    storeName: task.storeName || "全组织",
    level: task.status === "pending_approval" ? "warning" : "info",
    title: `${statusLabel(task.status)} · ${task.title}`,
    description: `${task.platform ? platformName(task.platform) : task.taskType}`
  }))];
  const insight = dailyGrowthInsight(stores, todayMap, yesterdayMap, pendingIssues.length);
  const platformToday = {
    eleme: [...todayMap.values()].reduce((sum, item) => sum + item.eleme.orders, 0),
    meituan: [...todayMap.values()].reduce((sum, item) => sum + item.meituan.orders, 0),
    jd: [...todayMap.values()].reduce((sum, item) => sum + item.jd.orders, 0)
  };
  const platformCoverage = [
    { key: "eleme", label: "淘宝闪购", orders: platformToday.eleme, mark: "淘" },
    { key: "meituan", label: "美团外卖", orders: platformToday.meituan, mark: "美" },
    { key: "jd", label: "京东秒送", orders: platformToday.jd, mark: "京" }
  ];

  return `
    <div class="metric-grid">
      ${metricCard("今日营收", money(todayTotal.revenue), `数据日期 ${today}`, "circle-dollar-sign")}
      ${metricCard("今日订单", number(todayTotal.orders), "三平台实时汇总", "receipt-text")}
      ${metricCard("昨日营收", money(yesterdayTotal.revenue), `数据日期 ${yesterday}`, "calendar-clock")}
      ${metricCard("昨日订单", number(yesterdayTotal.orders), "三平台全部门店", "receipt")}
      ${metricCard("待处理问题", number(pendingIssues.length), "异常、审批与执行中", "clipboard-check", "warning")}
      ${metricCard("执行端", `${dashboard.summary.devicesOnline} 在线`, "Mac 安全执行", "monitor-check")}
    </div>

    <section class="panel insight-band">
      <div class="insight-mark">${icon("sparkles")}</div>
      <div class="insight-main">
        <div class="insight-kicker">${escapeHtml(insight.headline)}</div>
        <p>${escapeHtml(insight.body)}</p>
        ${
          insight.tags.length
            ? `<div class="insight-tags">${insight.tags.map((tag) => `<span>${escapeHtml(tag)}</span>`).join("")}</div>`
            : ""
        }
      </div>
      <button class="insight-action" data-page="agent">${icon("arrow-right")}</button>
    </section>

    <section class="panel">
      <div class="panel-head">
        <div><h2>今日数据完整度</h2><span>${today} · 按平台回传口径</span></div>
        <button class="text-button" data-page="operations">${icon("refresh-cw")} 采集与导入</button>
      </div>
      <div class="coverage-grid">
        ${platformCoverage
          .map(
            (item) => `
              <article class="coverage-card ${item.orders > 0 ? "ready" : "pending"}">
                <span class="coverage-mark">${escapeHtml(item.mark)}</span>
                <div>
                  <strong>${escapeHtml(item.label)}</strong>
                  <small>${item.orders > 0 ? `${number(item.orders)} 单已入库` : "等待今日回传"}</small>
                </div>
              </article>
            `
          )
          .join("")}
      </div>
    </section>

    <section class="panel">
      <div class="panel-head">
        <div><h2>今日门店实时</h2><span>${today} · 按订单量排序，随平台回传实时更新</span></div>
        <button class="secondary-button" data-page="operations">${icon("scan-search")} 一键采集</button>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>门店</th><th>淘宝闪购</th><th>美团外卖</th><th>京东秒送</th><th>今日合计</th></tr></thead>
          <tbody>${todayStores.map((store, index) => storeDayRow(store, todayMap.get(store.id), index + 1)).join("")}</tbody>
        </table>
      </div>
    </section>

    <section class="panel">
      <div class="panel-head">
        <div><h2>昨日全部门店营业</h2><span>${yesterday} · 按营收排序</span></div>
        <button class="secondary-button" data-page="data">${icon("database-zap")} 经营数据</button>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>门店</th><th>淘宝闪购</th><th>美团外卖</th><th>京东秒送</th><th>昨日合计</th></tr></thead>
          <tbody>${yesterdayStores.map((store, index) => storeDayRow(store, yesterdayMap.get(store.id), index + 1)).join("")}</tbody>
        </table>
      </div>
    </section>

    <section class="panel">
      <div class="panel-head">
        <div><h2>连锁待处理问题</h2><span>异常、失败任务、待审批和进行中的动作</span></div>
        <button class="text-button" data-page="tasks">全部任务 ${icon("arrow-up-right")}</button>
      </div>
      <div class="list-stack">
        ${
          pendingIssues.length
            ? pendingIssues
                .slice(0, 8)
                .map(
                  (item) => `
                    <article class="alert-row ${escapeHtml(item.level)}">
                      <span class="alert-dot"></span>
                      <div><strong>${escapeHtml(item.title)}</strong><p>${escapeHtml(item.storeName)} · ${escapeHtml(item.description)}</p></div>
                    </article>
                  `
                )
                .join("")
            : emptyTemplate("当前没有待处理问题")
        }
      </div>
    </section>

    <section class="panel chart-panel">
      <div class="panel-head">
        <div><h2>营收与订单趋势</h2><span>最近 7 个自然日</span></div>
        <button class="text-button" data-page="data">导入数据 ${icon("arrow-up-right")}</button>
      </div>
      <div id="dashboard-chart"></div>
    </section>
  `;
}

function agentTemplate(): string {
  const answer = state.agentAnswer;
  const stores = state.dashboard?.stores || [];
  return `
    <section class="panel insight-band">
      <div class="insight-mark">${icon("sparkles")}</div>
      <div class="insight-main">
        <div class="insight-kicker">老板智能体</div>
        <p>结合真实经营数据做诊断与建议；所有平台写操作仍需老板审批和本机确认。</p>
      </div>
    </section>
    <div class="agent-layout">
      <section class="panel agent-command">
        <div class="agent-mark">${icon("sparkles")}</div>
        <div class="panel-head">
          <div><h2>直接问经营问题</h2><span>先给数据诊断，确认后再转成任务</span></div>
        </div>
        <form id="agent-form">
          <label>
            <span>老板指令</span>
            <textarea name="prompt" rows="5" required placeholder="例如：找出近 7 天转化下滑最严重的门店，给出三个以内整改动作，并标记需要我审批的事项。"></textarea>
          </label>
          <div class="agent-options">
            <label>
              <span>分析周期</span>
              <select name="periodDays">
                <option value="7">近 7 天</option>
                <option value="14">近 14 天</option>
                <option value="30">近 30 天</option>
              </select>
            </label>
            <label>
              <span>门店范围</span>
              <select name="storeIds" multiple size="5">
                <option value="">全部门店</option>
                ${stores.map((store) => `<option value="${escapeHtml(store.id)}">${escapeHtml(store.name)}</option>`).join("")}
              </select>
            </label>
          </div>
          <button class="primary-button" type="submit">${icon("send")} 开始分析</button>
        </form>
        <p class="agent-boundary">${icon("shield")} 智能体只能生成建议，所有平台写操作仍需审批与本机确认。</p>
      </section>
      <section class="panel agent-result">
        <div class="panel-head">
          <div><h2>分析结果</h2><span>${answer ? sourceLabel(answer.source) : "等待指令"}</span></div>
          ${
            answer
              ? `<button class="secondary-button" id="convert-actions">${icon("list-plus")} 转为任务</button>`
              : ""
          }
        </div>
        ${
          answer
            ? `
              <div class="answer-summary">${escapeHtml(answer.summary)}</div>
              <h3>关键发现</h3>
              <div class="finding-list">
                ${answer.findings
                  .map(
                    (item) => `
                      <article>
                        <strong>${escapeHtml(item.storeName || "全组织")}</strong>
                        <p>${escapeHtml(item.evidence)}</p>
                        <span>${escapeHtml(item.impact)}</span>
                      </article>
                    `
                  )
                  .join("")}
              </div>
              <h3>建议动作</h3>
              <div class="action-list">
                ${answer.recommendedActions
                  .map(
                    (item, index) => `
                      <label class="action-option">
                        <input type="checkbox" data-agent-action="${index}" checked />
                        <div>
                          <strong>${escapeHtml(item.title)}</strong>
                          <p>${escapeHtml(item.detail)}</p>
                          <span class="risk-tag ${item.riskLevel}">${escapeHtml(riskLabel(item.riskLevel))}</span>
                        </div>
                      </label>
                    `
                  )
                  .join("")}
              </div>
              ${
                answer.riskFlags.length
                  ? `<div class="risk-flags">${answer.riskFlags.map((flag) => `<span>${escapeHtml(flag)}</span>`).join("")}</div>`
                  : ""
              }
            `
            : emptyTemplate("输入问题后，智能体会结合经营数据和任务状态进行分析。")
        }
      </section>
    </div>
    <section class="panel period-report-panel">
      <div class="panel-head">
        <div><h2>周期经营报告</h2><span>按近 15/30/90 天自动生成诊断报告</span></div>
        <form id="period-report-form" class="inline-form">
          <select name="periodDays">
            <option value="15" selected>近 15 天</option>
            <option value="30">近 30 天</option>
            <option value="90">近 90 天</option>
          </select>
          <button class="primary-button" type="submit">${icon("file-text")} 生成报告</button>
        </form>
      </div>
      <div id="period-report-output">
        ${
          state.periodReport
            ? `<div class="period-report-markdown">${renderMarkdown(state.periodReport)}</div>`
            : emptyTemplate("选择周期后点击“生成报告”，系统会基于已入库数据自动产出诊断报告。")
        }
      </div>
    </section>
    <section class="panel codex-bind-panel">
      <div class="panel-head"><div><h2>绑定本机 Codex 指令</h2><span>把老板智能体动作直接绑定成一条本机 Codex 指令</span></div></div>
      <form class="stack-form" id="codex-instruction-form">
        <label><span>指令内容</span><textarea name="instruction" rows="5" required placeholder="例如：读取当前美团页面，找到门店经营报表或导出入口，只报告可安全点击的元素；不要提交任何写操作。"></textarea></label>
        <div class="form-grid">
          <label><span>平台</span><select name="platform">
            <option value="">不绑定平台</option>
            <option value="meituan">美团外卖</option>
            <option value="taobao_flash">淘宝闪购</option>
            <option value="jingdong">京东秒送</option>
          </select></label>
          <label><span>门店</span><select name="storeId"><option value="">全部门店</option>${stores.map((store) => `<option value="${escapeHtml(store.id)}">${escapeHtml(store.name)}</option>`).join("")}</select></label>
          <label><span>执行模式</span><select name="mode">
            <option value="read">只读分析</option>
            <option value="browser_action">浏览器动作（需本机确认）</option>
          </select></label>
        </div>
        <div class="agent-boundary">${icon("shield-check")} “浏览器动作”仅允许 Codex 生成只读、可回滚的页面步骤；检测到提交、改价、回复等写操作时会要求本机确认并停止。</div>
        <button class="primary-button" type="submit">${icon("terminal")} 绑定并执行 Codex 指令</button>
      </form>
    </section>
  `;
}

function storesTemplate(): string {
  const stores = state.dashboard?.stores || [];
  const owners = [...new Set(stores.map((store) => store.owner).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b, "zh-CN")
  );
  const today = localDateString(new Date());
  const todayStoreIds = new Set(
    state.metrics.filter((metric) => metric.metricDate === today).map((metric) => metric.storeId)
  );
  const activeCount = stores.filter((store) => store.status === "active").length;
  const fullyBoundCount = stores.filter(
    (store) => TAKEOUT_PLATFORM_IDS.filter((platform) => store.platformIds?.[platform]).length === 3
  ).length;
  const withTodayData = stores.filter((store) => todayStoreIds.has(store.id)).length;
  return `
    <section class="panel insight-band store-overview">
      <div class="insight-mark">${icon("store")}</div>
      <div class="insight-main">
        <div class="insight-kicker">门店矩阵概览</div>
        <p>${stores.length} 家门店 · ${activeCount} 家营业中 · ${fullyBoundCount} 家三平台绑定完整 · ${withTodayData} 家今日有数据。</p>
      </div>
    </section>
    <section class="panel">
      <div class="toolbar">
        <label class="search-box">${icon("search")}<input id="store-search" placeholder="搜索门店或城市" /></label>
        <select id="owner-filter">
          <option value="">全部负责人</option>
          ${owners.map((owner) => `<option value="${escapeHtml(owner)}">${escapeHtml(owner)}</option>`).join("")}
        </select>
        <select id="status-filter"><option value="">全部状态</option><option value="active">营业</option><option value="inactive">停用</option></select>
        <button class="secondary-button" type="button" id="reset-store-filters">${icon("rotate-ccw")} 重置</button>
        <button class="primary-button" type="button" id="add-platform-binding">${icon("plus")} 添加平台绑定</button>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>门店</th><th>城市</th><th>负责人</th><th>状态</th><th>近 7 天营收</th><th>订单</th><th>转化率</th><th>平台绑定</th></tr></thead>
          <tbody id="store-table-body">
            ${stores.map(storeRow).join("")}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function storeRow(store: DashboardData["stores"][number]): string {
  const boundCount = TAKEOUT_PLATFORM_IDS.filter(
    (platform) => store.platformIds?.[platform]
  ).length;
  return `
    <tr data-store-row data-city="${escapeHtml(store.city)}" data-owner="${escapeHtml(store.owner)}" data-status="${escapeHtml(store.status)}">
      <td><strong>${escapeHtml(store.name)}</strong><small>${escapeHtml(store.id)}</small></td>
      <td>${escapeHtml(store.city)}</td>
      <td>${escapeHtml(store.owner)}</td>
      <td><span class="status-pill">${store.status === "active" ? "营业" : "停用"}</span></td>
      <td>${money(store.revenue)}</td>
      <td>${number(store.orders)}</td>
      <td>${store.visitors > 0 ? `${store.conversion.toFixed(1)}%` : "--"}</td>
      <td>
        <div class="platform-binding-cell">
          <div class="platform-dots">${TAKEOUT_PLATFORM_IDS.map((platform) => `<span class="${store.platformIds?.[platform] ? "bound" : ""}" title="${escapeHtml(platformName(platform))}">${cloudPlatformMark(platform)}</span>`).join("")}</div>
          <small class="binding-count ${boundCount === 3 ? "complete" : ""}">${boundCount}/3</small>
          <button class="text-button" type="button" data-manage-platforms="${escapeHtml(store.id)}">管理</button>
        </div>
      </td>
    </tr>
  `;
}

function promotionPolicyCard(policy: PromotionPolicy): string {
  const modeLabel = policy.mode === "guarded_auto" ? "受控自动" : "只给建议";
  return `
    <article class="promotion-policy-card">
      <div class="promotion-policy-head">
        <span class="platform-result-mark">${cloudPlatformMark(policy.platform)}</span>
        <div><strong>${escapeHtml(policy.platformName)}</strong><small>${escapeHtml(modeLabel)} · ${policy.enabled ? "自动评估已启用" : "自动评估未启用"}</small></div>
        <span class="status-pill ${policy.enabled ? "ready" : "pending_approval"}">${policy.enabled ? "已启用" : "待启用"}</span>
      </div>
      <div class="promotion-policy-metrics">
        <span>日预算上限 <strong>${money(policy.dailyBudgetCap)}</strong></span>
        <span>出价上限 <strong>${money(policy.bidCap)}</strong></span>
        <span>单次调整 <strong>${policy.maxChangePercent}%</strong></span>
        <span>目标 ROI <strong>${policy.targetRoi}</strong></span>
        <span>数据窗口 <strong>${escapeHtml(policy.preferredTimeRange)} · ${policy.metricWindowDays}天</strong></span>
        <span>观察期 <strong>${policy.observationDays}天</strong></span>
      </div>
      <p>${escapeHtml(policy.notes)}</p>
      <button class="secondary-button compact" type="button" data-promotion-settings="${policy.platform}">
        ${icon("settings-2")} 设置${escapeHtml(policy.platformName.replace("外卖", ""))}自动推广
      </button>
    </article>
  `;
}

function promotionPlanCard(plan: TakeoutPlan): string {
  const statusText: Record<TakeoutPlan["status"], string> = {
    draft: "草稿",
    approved: "已批准",
    executing: "执行中",
    completed: "已完成",
    cancelled: "已取消"
  };
  return `
    <article class="promotion-plan-row">
      <span class="platform-result-mark">${cloudPlatformMark(plan.platform)}</span>
      <div>
        <strong>${escapeHtml(plan.title)}</strong>
        <p>${escapeHtml(plan.storeName)} · 预算 ${money(plan.budget)} · 目标 ROI ${plan.targetRoi}</p>
      </div>
      <span class="status-pill ${plan.status === "completed" ? "succeeded" : plan.status}">${escapeHtml(statusText[plan.status])}</span>
      <div class="promotion-plan-actions">
        ${
          plan.status === "draft"
            ? `<button class="secondary-button compact" data-plan-status="approved" data-plan-id="${escapeHtml(plan.id)}">批准</button>`
            : ""
        }
        ${
          plan.status === "approved"
            ? `<button class="secondary-button compact" data-plan-status="executing" data-plan-id="${escapeHtml(plan.id)}">开始执行</button>`
            : ""
        }
        ${
          plan.status === "executing"
            ? `<button class="secondary-button compact" data-plan-status="completed" data-plan-id="${escapeHtml(plan.id)}">完成</button>`
            : ""
        }
        ${
          !["completed", "cancelled"].includes(plan.status)
            ? `<button class="text-button danger-text" data-plan-status="cancelled" data-plan-id="${escapeHtml(plan.id)}">取消</button>`
            : ""
        }
      </div>
    </article>
  `;
}

function platformSummary(metrics: MetricRecord[], platformAdSpend: Record<string, number> = {}, days = 7): Array<{
  key: string;
  label: string;
  mark: string;
  revenue: number;
  orders: number;
  adSpend: number;
  visitors: number;
  roi: number;
}> {
  const now = new Date();
  const start = localDateString(new Date(now.getTime() - (days - 1) * 24 * 60 * 60 * 1000));
  const buckets: Record<string, { label: string; mark: string; revenue: number; orders: number; adSpend: number; visitors: number }> = {
    eleme_takeout: { label: "淘宝闪购", mark: "淘", revenue: 0, orders: 0, adSpend: 0, visitors: 0 },
    meituan_takeout: { label: "美团外卖", mark: "美", revenue: 0, orders: 0, adSpend: 0, visitors: 0 },
    jd_takeout: { label: "京东秒送", mark: "京", revenue: 0, orders: 0, adSpend: 0, visitors: 0 }
  };
  for (const metric of metrics) {
    if (metric.metricDate < start) continue;
    const bucket = buckets[metric.channel];
    if (!bucket) continue;
    bucket.revenue += Number(metric.revenue || 0);
    bucket.orders += Number(metric.orders || 0);
    bucket.visitors += Number(metric.visitors || 0);
  }
  return Object.entries(buckets).map(([key, bucket]) => ({
    key,
    label: bucket.label,
    mark: bucket.mark,
    revenue: bucket.revenue,
    orders: bucket.orders,
    adSpend: Number(platformAdSpend[key] || bucket.adSpend || 0),
    visitors: bucket.visitors,
    roi: (Number(platformAdSpend[key] || 0) || bucket.adSpend) > 0
      ? bucket.revenue / (Number(platformAdSpend[key] || 0) || bucket.adSpend)
      : 0
  }));
}

function operationsTemplate(): string {
  const stores = state.dashboard?.stores || [];
  const onlineDevices = state.devices.filter((device) => device.status === "online").length;
  const promotionPolicies = TAKEOUT_PLATFORM_IDS.map(promotionPolicyFor);
  const allPromotionPlans = state.takeoutPlans.filter((plan) => plan.planType === "promotion");
  const promotionPlans = allPromotionPlans.slice(0, 18);
  const defaultIntegrations: TakeoutIntegration[] = [
    {
      id: "meituan",
      name: "美团外卖",
      authorizationMode: "品牌商或服务商授权",
      applicationUrl: "https://developer.meituan.com/",
      documentationUrl: "https://developer.meituan.com/",
      sdkUrl: "",
      callbackUrl: "https://api.fanhegushi.com/callback/meituan",
      capabilities: ["门店读取", "商品读取", "订单读取", "推广读取"],
      credentialConfigured: false,
      status: "not_configured"
    },
    {
      id: "taobao_flash",
      name: "淘宝闪购 / 饿了么",
      authorizationMode: "商家身份授权",
      applicationUrl: "https://open.shop.ele.me/",
      documentationUrl: "https://open.shop.ele.me/",
      sdkUrl: "",
      callbackUrl: "https://api.fanhegushi.com/callback/taobao-flash",
      capabilities: ["门店读取", "商品读取", "订单读取", "推广读取"],
      credentialConfigured: false,
      status: "not_configured"
    },
    {
      id: "jingdong",
      name: "京东秒送",
      authorizationMode: "企业应用授权",
      applicationUrl: "https://open.jd.com/",
      documentationUrl: "https://open.jd.com/",
      sdkUrl: "",
      callbackUrl: "https://api.fanhegushi.com/callback/jingdong",
      capabilities: ["门店读取", "商品读取", "订单读取", "推广读取"],
      credentialConfigured: false,
      status: "not_configured"
    }
  ];
  const integrations = state.integrations.length ? state.integrations : defaultIntegrations;
  const platformOverview = platformSummary(state.metrics, state.dashboard?.platformAdSpend || {});
  return `
    <section class="panel">
      <div class="panel-head">
        <div><h2>三平台经营概览</h2><span>近 7 天 · 按已入库经营数据汇总</span></div>
        <button class="text-button" data-page="data">${icon("database-zap")} 经营数据</button>
      </div>
      <div class="platform-overview-grid">
        ${platformOverview
          .map(
            (item) => `
              <article class="platform-overview-card">
                <span class="coverage-mark">${escapeHtml(item.mark)}</span>
                <div class="platform-overview-head">
                  <strong>${escapeHtml(item.label)}</strong>
                  <span class="roi-pill">ROI ${item.roi > 0 ? item.roi.toFixed(2) : "--"}</span>
                </div>
                <div class="platform-overview-stats">
                  <div><small>营收</small><strong>${money(item.revenue)}</strong></div>
                  <div><small>订单</small><strong>${number(item.orders)}</strong></div>
                  <div><small>推广花费</small><strong>${money(item.adSpend)}</strong></div>
                </div>
              </article>
            `
          )
          .join("")}
      </div>
    </section>
    <section class="panel platform-tool-panel">
      <div class="panel-head">
        <div><h2>三平台自动采集（本机 Codex）</h2><span>Mac 登录官方后台后由本机 Codex 读取页面并提取数据，不依赖官方 API 凭证</span></div>
        <span class="status-pill ${onlineDevices ? "online" : "offline"}">${onlineDevices ? `${onlineDevices} 台执行端在线` : "执行端离线"}</span>
      </div>
      <div class="platform-tool-body">
        <div class="platform-tool-intro">
          <span class="platform-tool-icon">${icon("panels-top-left")}</span>
          <div>
            <strong>本机 Codex 采集通道</strong>
            <p>${onlineDevices ? "执行端在线，可读取已登录平台页面并入库；官方 API 授权是另一条可选通道。" : "任务仍可创建并排队，Mac 执行端上线后自动领取。"}</p>
          </div>
        </div>
        <div class="platform-tool-actions">
          <button class="secondary-button" id="open-platforms-button" ${state.platformToolBusy ? "disabled" : ""}>
            ${icon(state.platformToolBusy === "open" ? "loader-circle" : "external-link")}
            ${state.platformToolBusy === "open" ? "正在创建" : "打开并登录三平台"}
          </button>
          <button class="primary-button" id="collect-platforms-button" ${state.platformToolBusy ? "disabled" : ""}>
            ${icon(state.platformToolBusy === "collect" ? "loader-circle" : "scan-search")}
            ${state.platformToolBusy === "collect" ? "正在采集" : "一键采集三平台"}
          </button>
          <button class="secondary-button" id="promotion-plan-button" ${state.platformToolBusy ? "disabled" : ""}>
            ${icon(state.platformToolBusy === "promotion" ? "loader-circle" : "circle-dollar-sign")}
            ${state.platformToolBusy === "promotion" ? "正在生成" : "推广自动调优"}
          </button>
          <button class="secondary-button" id="collect-ad-spend-button">
            ${icon("receipt")} 采集推广消耗
          </button>
          <button class="secondary-button" id="create-store-audit-tasks">
            ${icon("list-checks")} 逐店采集全部
          </button>
          <button class="secondary-button" id="export-platform-reports">
            ${icon("download")} 自动导出报表
          </button>
          <button class="secondary-button" id="open-edge-login-button">
            ${icon("monitor-up")} 打开 Edge 登录
          </button>
        </div>
      </div>
      ${platformTaskOutput(state.platformTask)}
    </section>
    <section class="panel">
      <div class="panel-head">
        <div><h2>三平台自动推广</h2><span>每个平台单独设置预算、出价、目标 ROI 和止损标准；平台写入仍需审批与本机确认</span></div>
        <button class="primary-button" type="button" id="generate-promotion-plans">${icon("calendar-plus")} 生成三平台推广计划</button>
      </div>
      <div class="promotion-policy-grid">
        ${promotionPolicies.map(promotionPolicyCard).join("")}
      </div>
      <div class="promotion-standard">
        <strong>统一底线</strong>
        <span>数据不完整不调；单次不超过设置上限；连续观察期内不重复改；ROI 低于止损线先暂停加码；所有写操作必须审批和本机确认。</span>
      </div>
      <div class="promotion-plan-section">
        <div class="promotion-plan-head">
          <strong>推广计划</strong>
          <span>${allPromotionPlans.length} 个计划${allPromotionPlans.length > promotionPlans.length ? "，当前显示最近 18 个" : ""}</span>
        </div>
        <div class="promotion-plan-list">
          ${promotionPlans.length ? promotionPlans.map(promotionPlanCard).join("") : emptyTemplate("尚未生成推广计划。点击右上角按钮为全部门店生成三平台计划。")}
        </div>
      </div>
    </section>
    <div class="module-grid">
      ${integrations
        .map(
          (integration) => {
            const boundStores = stores.filter((store) => store.platformIds?.[integration.id]).length;
            const statusLabel = integration.credentialConfigured
              ? "官方凭证已配置"
              : boundStores > 0
                ? "门店绑定已导入，凭证待配置"
                : "待申请";
            const iconName =
              integration.id === "meituan" ? "badge-percent" : integration.id === "taobao_flash" ? "shopping-bag" : "truck";
            return `
            <article class="module-card">
              <span class="module-icon">${icon(iconName)}</span>
              <div><h2>${escapeHtml(integration.name)}</h2><p>${escapeHtml(integration.authorizationMode)} · ${boundStores} 家门店已绑定</p></div>
              <span class="status-pill ${integration.credentialConfigured ? "ready" : ""}">${escapeHtml(statusLabel)}</span>
              <div class="button-row">
                <a class="secondary-button" href="${escapeHtml(integration.applicationUrl)}" target="_blank" rel="noreferrer">申请入口</a>
                <button class="secondary-button" data-open-integration="${escapeHtml(integration.id)}">${icon("external-link")} 官方后台</button>
                <button class="primary-button" data-create-platform-task="${escapeHtml(integration.id)}">${icon("plus")} 检查任务</button>
              </div>
            </article>
          `;
          }
        )
        .join("")}
    </div>
    <section class="panel">
      <div class="panel-head"><div><h2>平台接入明细</h2><span>门店ID仅用于定位门店；订单、退款和推广结构化同步还需要平台签发的 AppKey、AppSecret 和商家授权</span></div></div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>平台</th><th>授权方式</th><th>已绑定门店</th><th>API 凭证</th><th>回调地址</th></tr></thead>
          <tbody>
            ${integrations
              .map((integration) => {
                const boundStores = stores.filter((store) => store.platformIds?.[integration.id]).length;
                return `<tr>
                  <td><strong>${escapeHtml(integration.name)}</strong></td>
                  <td>${escapeHtml(integration.authorizationMode)}</td>
                  <td>${boundStores} / ${stores.length}</td>
                  <td><span class="status-pill ${integration.credentialConfigured ? "ready" : "pending_approval"}">${integration.credentialConfigured ? "已配置" : "待配置"}</span></td>
                  <td><code>${escapeHtml(integration.callbackUrl)}</code></td>
                </tr>`;
              })
              .join("")}
          </tbody>
        </table>
      </div>
    </section>
    <section class="panel">
      <div class="panel-head"><div><h2>运营计划闭环</h2><span>草稿、批准、执行和结果回读</span></div><button class="secondary-button" data-page="tasks">${icon("clipboard-list")} 查看任务</button></div>
      <div class="process-flow">
        ${["数据读取", "老板审批", "Mac 领取", "人工确认", "平台回读", "结果归档"]
          .map((item, index) => `<div><span>${index + 1}</span><strong>${item}</strong></div>`)
          .join("")}
      </div>
    </section>
  `;
}

function dataTemplate(): string {
  const metrics = [...state.metrics]
    .sort((a, b) => b.metricDate.localeCompare(a.metricDate))
    .slice(0, 100);
  return `
    <div class="two-column">
      <section class="panel">
        <div class="panel-head"><div><h2>导入经营报表</h2><span>CSV/XLSX 先预览校验，再确认入库</span></div></div>
        <div class="template-links">
          <span>平台报表模板</span>
          <button class="text-button" type="button" data-template-platform="meituan">美团</button>
          <button class="text-button" type="button" data-template-platform="taobao_flash">淘宝闪购</button>
          <button class="text-button" type="button" data-template-platform="jingdong">京东秒送</button>
        </div>
        <form id="import-form" class="stack-form">
          <label class="drop-zone" id="file-drop">
            ${icon("file-spreadsheet")}
            <strong>选择或拖入平台报表</strong>
            <span>支持 .csv、.xlsx，单文件不超过 10 MB</span>
            <input type="file" name="file" accept=".csv,.xlsx" hidden />
          </label>
          <button class="primary-button" type="submit">${icon("scan-search")} 预览并校验</button>
        </form>
        <div id="import-result"></div>
      </section>
      <section class="panel">
        <div class="panel-head"><div><h2>手工录入</h2><span>单门店、单日期、单渠道补充数据</span></div></div>
        <form class="stack-form" id="metric-form">
          <div class="form-grid">
            <label><span>门店</span><select name="storeId">${(state.dashboard?.stores || []).map((store) => `<option value="${escapeHtml(store.id)}">${escapeHtml(store.name)}</option>`).join("")}</select></label>
            <label><span>日期</span><input type="date" name="metricDate" required /></label>
            <label><span>渠道</span><select name="channel"><option value="meituan_takeout">美团外卖</option><option value="eleme_takeout">淘宝闪购</option><option value="jd_takeout">京东秒送</option><option value="groupbuy">团购</option></select></label>
            <label><span>营收</span><input type="number" name="revenue" min="0" step="0.01" required /></label>
            <label><span>订单</span><input type="number" name="orders" min="0" required /></label>
            <label><span>访客</span><input type="number" name="visitors" min="0" required /></label>
            <label><span>推广花费</span><input type="number" name="adSpend" min="0" step="0.01" /></label>
          </div>
          <button class="primary-button" type="submit">${icon("save")} 保存指标</button>
        </form>
      </section>
    </div>
    <section class="panel">
      <div class="panel-head"><div><h2>已入库经营数据</h2><span>按日期、门店和平台展示实际数据，日期就是数据所属的经营日</span></div></div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>日期</th><th>门店</th><th>平台</th><th>营收</th><th>订单</th><th>访客</th><th>推广花费</th></tr></thead>
          <tbody>
            ${metrics.length
              ? metrics.map((metric) => {
                  const store = state.dashboard?.stores.find((item) => item.id === metric.storeId);
                  return `
                    <tr>
                      <td><strong>${escapeHtml(metric.metricDate)}</strong></td>
                      <td>${escapeHtml(store?.name || metric.storeId)}</td>
                      <td>${escapeHtml(metricChannelLabel(metric.channel))}</td>
                      <td>${money(metric.revenue)}</td>
                      <td>${number(metric.orders)}</td>
                      <td>${number(metric.visitors)}</td>
                      <td>${money(metric.adSpend)}</td>
                    </tr>
                  `;
                }).join("")
              : `<tr><td colspan="7">${emptyTemplate("还没有入库经营数据")}</td></tr>`}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function platformReportTemplate(platform: TakeoutPlatformId): string {
  const channel =
    platform === "meituan"
      ? "美团外卖"
      : platform === "taobao_flash"
        ? "淘宝闪购"
        : "京东秒送";
  const sample =
    platform === "meituan"
      ? "饭盒故事（德州店）"
      : platform === "taobao_flash"
        ? "饭盒故事（德州店）"
        : "饭盒故事（德州店）";
  return `门店名称,日期,渠道,营收,订单数,访客数,推广花费\n${sample},2026-09-25,${channel},0,0,0,0`;
}

function downloadTextFile(filename: string, content: string): void {
  const blob = new Blob(["\uFEFF" + content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function contentTemplate(): string {
  const items = [
    ["外卖定价", "TAKEOUT_PRICING", "结合成本、竞品和目标毛利生成价格区间", "circle-dollar-sign"],
    ["活动配置", "PROMOTION_PLAN", "制定满减、折扣和投放时段", "badge-percent"],
    ["菜品优化", "MENU_OPTIMIZE", "重组引流款、利润款和套餐", "utensils"],
    ["团购套餐", "GROUP_PACKAGE", "生成双人及多人团购组合", "tickets"],
    ["评价回复", "REVIEW_REPLY", "按好评、中评和差评生成回复", "message-square-reply"],
    ["小红书内容", "XHS_POST", "生成标题、正文、话题和互动引导", "book-heart"],
    ["短视频脚本", "VIDEO_SCRIPT", "生成钩子、分镜和口播", "clapperboard"]
  ];
  return `
    <section class="panel insight-band content-hero">
      <div class="insight-mark">${icon("sparkles")}</div>
      <div class="insight-main">
        <div class="insight-kicker">AI 运营中心</div>
        <p>一次选择任务，AI 生成可直接执行的餐饮运营方案；结果用于人工发布，不自动改动平台。</p>
      </div>
    </section>
    <div class="module-grid compact">
      ${items
        .map(
          ([title, type, description, iconName]) => `
            <button class="quick-card" data-content-task="${type}">
              <span>${icon(String(iconName))}</span>
              <strong>${escapeHtml(title)}</strong>
              <small>${escapeHtml(description)}</small>
            </button>
          `
        )
        .join("")}
    </div>
    <section class="panel">
      <div class="panel-head"><div><h2>内容任务</h2><span>结果带 AI 标识，发布仍由人工完成</span></div></div>
      <div id="content-task-output">${emptyTemplate("选择上方能力创建内容任务。")}</div>
    </section>
  `;
}

function mediaTemplate(): string {
  return `
    <section class="panel insight-band">
      <div class="insight-mark">${icon("image-play")}</div>
      <div class="insight-main">
        <div class="insight-kicker">媒体中心</div>
        <p>图片与视频经云端处理后生成可用素材；发布前由人工确认。</p>
      </div>
    </section>
    <div class="two-column">
      <section class="panel">
        <div class="panel-head"><div><h2>图片素材</h2><span>头像、店招、横幅和套餐背景</span></div></div>
        <div class="upload-area">
          ${icon("image-plus")}
          <strong>上传原始图片</strong>
          <span>支持 JPG、PNG、WEBP，上传后创建图片处理任务</span>
          <input id="media-image-file" type="file" accept="image/*" hidden />
          <button class="secondary-button" data-upload-media="image">选择图片</button>
        </div>
      </section>
      <section class="panel">
        <div class="panel-head"><div><h2>视频剪辑</h2><span>服务器 FFmpeg 渲染，人工确认后下载</span></div></div>
        <div class="upload-area">
          ${icon("film")}
          <strong>上传视频片段</strong>
          <span>支持 MP4、MOV、M4V，单文件不超过 200 MB</span>
          <input id="media-video-file" type="file" accept="video/*" hidden />
          <button class="secondary-button" data-upload-media="video">选择视频</button>
        </div>
      </section>
    </div>
    <section class="panel">
      <div class="panel-head"><div><h2>媒体任务记录</h2><span>上传、渲染和下载状态</span></div></div>
      <div class="asset-grid">
        ${state.assets.length
          ? state.assets
              .map(
                (asset) => `
                  <article class="asset-card">
                    ${
                      asset.type === "image"
                        ? `<img src="${escapeHtml(asset.url)}" alt="${escapeHtml(asset.storeName || "经营素材")}" loading="lazy" />`
                        : `<span class="asset-file-icon">${icon("file-video")}</span>`
                    }
                    <strong>${escapeHtml(
                      asset.storeName ||
                        String(asset.metadata?.filename || "") ||
                        (asset.type === "video" ? "视频素材" : asset.type === "document" ? "文档素材" : "图片素材")
                    )}</strong>
                    <small>${escapeHtml(asset.type === "image" ? "图片" : asset.type === "video" ? "视频" : "文档")}</small>
                    <small>${escapeHtml(new Date(asset.createdAt).toLocaleString("zh-CN"))}</small>
                  </article>
                `
              )
              .join("")
          : emptyTemplate("暂无媒体素材")}
      </div>
      <div id="media-task-output"></div>
    </section>
  `;
}

function tasksTemplate(): string {
  const tasks = state.tasks;
  const taskStats = {
    pending: tasks.filter((task) => task.status === "pending_approval").length,
    running: tasks.filter((task) => ["claimed", "running", "local_confirmation_required"].includes(task.status)).length,
    failed: tasks.filter((task) => task.status === "failed").length,
    succeeded: tasks.filter((task) => task.status === "succeeded").length
  };
  return `
    <section class="panel insight-band task-overview">
      <div class="insight-mark">${icon("clipboard-check")}</div>
      <div class="insight-main">
        <div class="insight-kicker">任务概览</div>
        <p>${taskStats.pending} 待审批 · ${taskStats.running} 执行中 · ${taskStats.failed} 失败 · ${taskStats.succeeded} 已完成。</p>
      </div>
    </section>
    <section class="panel">
      <div class="panel-head">
        <div><h2>任务队列</h2><span>${tasks.length} 条记录</span></div>
        <button class="primary-button" id="new-task-button">${icon("plus")} 新建任务</button>
      </div>
      <div class="filter-tabs" id="task-filter">
        <button class="active" data-status="">全部</button>
        <button data-status="pending_approval">待审批</button>
        <button data-status="approved">已批准</button>
        <button data-status="running">执行中</button>
        <button data-status="succeeded">已完成</button>
        <button data-status="failed">失败</button>
        <button data-status="cancelled">已取消</button>
      </div>
      <div class="task-list" id="task-list">
        ${tasks.length ? tasks.map(taskCard).join("") : emptyTemplate("还没有任务。可以从老板智能体建议或上方按钮创建。")}
      </div>
    </section>
  `;
}

function taskCard(task: OpsTask): string {
  const canApprove = task.status === "pending_approval";
  const canCancel = !["succeeded", "failed", "cancelled"].includes(task.status);
  const canViewResult = Boolean(task.result) && ["succeeded", "failed"].includes(task.status);
  const canRetry = task.status === "failed";
  return `
    <article class="task-card" data-task-id="${escapeHtml(task.id)}" data-task-status="${escapeHtml(task.status)}">
      <div class="task-main">
        <div class="task-title-row">
          <span class="priority ${task.priority}">${escapeHtml(task.priority)}</span>
          <strong>${escapeHtml(task.title)}</strong>
          <span class="status-pill ${escapeHtml(task.status)}">${escapeHtml(statusLabel(task.status))}</span>
        </div>
        <p>${escapeHtml(task.detail || "无补充说明")}</p>
        <div class="task-meta">
          <span>${escapeHtml(task.storeName || "全组织")}</span>
          <span>${escapeHtml(task.platform || task.taskType)}</span>
          <span>${escapeHtml(riskLabel(task.riskLevel))}</span>
          <span>${escapeHtml(relativeTime(task.createdAt))}</span>
        </div>
      </div>
      <div class="task-actions">
        ${canViewResult ? `<button class="secondary-button compact" data-task-result>${icon("file-search")} 查看结果</button>` : ""}
        ${canRetry ? `<button class="secondary-button compact" data-task-transition="queued">${icon("rotate-ccw")} 重试</button>` : ""}
        ${canApprove ? `<button class="primary-button compact" data-task-transition="approved">${icon("check")} 批准</button>` : ""}
        ${canCancel ? `<button class="secondary-button compact" data-task-transition="cancelled">${icon("x")} 取消</button>` : ""}
      </div>
    </article>
  `;
}

function taskResultText(task: OpsTask): string {
  const result = task.result || {};
  const nested = result.result && typeof result.result === "object"
    ? (result.result as Record<string, unknown>)
    : {};
  const candidates = [
    result.content,
    nested.content,
    result.analysis,
    nested.analysis
  ];
  const content = candidates.find(
    (value): value is string => typeof value === "string" && value.trim().length > 0
  );
  if (content) return content;
  return JSON.stringify(result, null, 2).slice(0, 12000);
}

function taskResultPreview(task: OpsTask): string {
  if (!task.result || !["succeeded", "failed"].includes(task.status)) return "";
  const text = task.status === "failed" ? task.errorMessage || taskResultText(task) : taskResultText(task);
  return `
    <div class="inline-task-result">
      <div><strong>${task.status === "failed" ? "失败原因" : "执行结果"}</strong><span>${escapeHtml(relativeTime(task.updatedAt))}</span></div>
      <pre>${escapeHtml(text.slice(0, 12000))}</pre>
    </div>
  `;
}

function openTaskResult(task: OpsTask): void {
  const dialog = document.createElement("dialog");
  dialog.className = "modal";
  dialog.innerHTML = `
    <div class="modal-card task-result-modal">
      <div class="panel-head">
        <div><h2>${escapeHtml(task.title)}</h2><span>${escapeHtml(statusLabel(task.status))} · ${escapeHtml(relativeTime(task.updatedAt))}</span></div>
        <button class="icon-button" type="button" data-close-result aria-label="关闭">${icon("x")}</button>
      </div>
      <div class="task-result-meta">
        <span>${escapeHtml(task.storeName || "全组织")}</span>
        <span>${escapeHtml(task.platform || task.taskType)}</span>
        <span class="risk-tag ${escapeHtml(task.riskLevel)}">${escapeHtml(riskLabel(task.riskLevel))}</span>
      </div>
      <pre class="task-result-full">${escapeHtml((task.status === "failed" ? task.errorMessage || taskResultText(task) : taskResultText(task)).slice(0, 20000))}</pre>
    </div>
  `;
  document.body.append(dialog);
  refreshIcons();
  dialog.showModal();
  dialog.querySelector("[data-close-result]")?.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener("close", () => dialog.remove());
}

function devicesTemplate(): string {
  const active = state.pairingCodes.find((item) => new Date(item.expiresAt).getTime() > Date.now());
  return `
    <div class="two-column">
      <section class="panel">
        <div class="panel-head"><div><h2>Mac 执行端</h2><span>平台密码仅保存在对应设备钥匙串</span></div><button class="secondary-button" id="refresh-devices">${icon("refresh-cw")} 刷新</button></div>
        <div class="device-list">
          ${state.devices.length ? state.devices.map(deviceCard).join("") : emptyTemplate("还没有配对的执行端")}
        </div>
      </section>
      <section class="panel pairing-panel">
        <div class="panel-head"><div><h2>配对新设备</h2><span>配对码 10 分钟有效且仅可使用一次</span></div></div>
        ${
          active
            ? `
              <div class="pair-code"><small>在 Mac 执行端输入</small><strong>${escapeHtml(active.code)}</strong><span>${new Date(active.expiresAt).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })} 前有效</span></div>
              <button class="secondary-button wide" id="new-pair-code">${icon("rotate-ccw")} 重新生成</button>
            `
            : `<button class="primary-button wide" id="new-pair-code">${icon("key-round")} 生成配对码</button>`
        }
        <ol class="pairing-steps">
          <li><span>1</span>在 Mac 打开新执行端</li>
          <li><span>2</span>输入上方一次性配对码</li>
          <li><span>3</span>选择要绑定的平台账号</li>
        </ol>
      </section>
    </div>
  `;
}

function deviceCard(device: DeviceInfo): string {
  return `
    <article class="device-card">
      <span class="device-icon">${icon("monitor-check")}</span>
      <div>
        <strong>${escapeHtml(device.name)}</strong>
        <p>${escapeHtml(device.platform)} · ${device.status === "online" ? "在线" : `上次在线 ${relativeTime(device.lastSeenAt)}`}</p>
      </div>
      <span class="status-pill ${device.status}">${escapeHtml(statusLabel(device.status))}</span>
      ${device.status !== "revoked" ? `<button class="text-button danger-text" data-revoke-device="${escapeHtml(device.id)}">撤销</button>` : ""}
    </article>
  `;
}

function subscriptionTemplate(): string {
  const organization = state.user?.organization;
  if (!organization) return "";
  return `
    <section class="subscription-hero">
      <div>
        <p class="eyebrow">当前套餐</p>
        <h2>${escapeHtml(organization.planName)}</h2>
        <p>${new Date(organization.expiresAt).toLocaleDateString("zh-CN")} 到期</p>
      </div>
      <div class="point-balance">
        <span>${number(organization.aiPointsBalance)}</span>
        <small>剩余 AI 点数</small>
      </div>
      <span class="status-pill ready">${escapeHtml(organization.status)}</span>
    </section>
    <div class="metric-grid three">
      ${metricCard("每月点数", number(organization.aiPointsMonthly), "订阅重置", "sparkles")}
      ${metricCard("已授权门店", number(state.dashboard?.summary.stores || 0), "当前组织门店数", "store")}
      ${metricCard("本机 Codex", "不计点", "执行端独立运行", "terminal")}
    </div>
    <section class="panel">
      <div class="panel-head"><div><h2>激活码兑换</h2><span>购买后由平台生成，不在页面展示在线支付</span></div></div>
      <form class="inline-form" id="redeem-form"><input name="code" placeholder="输入激活码" required /><button class="primary-button" type="submit">${icon("ticket-check")} 兑换</button></form>
    </section>
  `;
}

function settingsTemplate(): string {
  return `
    <div class="two-column">
      <section class="panel">
        <div class="panel-head"><div><h2>修改密码</h2><span>至少 10 位，建议同时包含数字、字母和符号</span></div></div>
        <form class="stack-form" id="password-form">
          <label><span>新密码</span><input name="password" type="password" minlength="10" required /></label>
          <label><span>确认新密码</span><input name="confirmPassword" type="password" minlength="10" required /></label>
          <button class="primary-button" type="submit">${icon("shield-check")} 保存新密码</button>
        </form>
      </section>
      <section class="panel">
        <div class="panel-head"><div><h2>运行状态</h2><span>云端与本机执行边界</span></div></div>
        <div class="status-list">
          <div><span>统一数据源</span><strong>PostgreSQL</strong></div>
          <div><span>平台密码</span><strong>仅 Mac 钥匙串</strong></div>
          <div><span>云端 AI</span><strong>由服务端配置</strong></div>
          <div><span>高风险写操作</span><strong>双重确认</strong></div>
        </div>
      </section>
    </div>
  `;
}

function loadingTemplate(message: string): string {
  return `<section class="panel loading-state"><span class="spinner"></span><strong>${escapeHtml(message)}</strong></section>`;
}

function emptyTemplate(message: string): string {
  return `<div class="empty-state">${icon("inbox")}<span>${escapeHtml(message)}</span></div>`;
}

function metricCard(label: string, value: string, note: string, iconName: string, tone = ""): string {
  return `
    <article class="metric-card ${tone}">
      <span class="metric-icon">${icon(iconName)}</span>
      <div><small>${escapeHtml(label)}</small><strong>${escapeHtml(value)}</strong><p>${escapeHtml(note)}</p></div>
    </article>
  `;
}

function riskLabel(risk: TaskRiskLevel): string {
  if (risk === "high") return "高风险";
  if (risk === "medium") return "中风险";
  return "低风险";
}

function sourceLabel(source: BossAgentAnswer["source"]): string {
  if (source === "cloud_ai") return "云端 AI 分析";
  if (source === "local_codex") return "本机 Codex 分析";
  return "规则分析，未调用模型";
}

const PLATFORM_NAMES: Record<string, string> = {
  eleme: "淘宝闪购 / 饿了么",
  meituan: "美团外卖",
  jd: "京东秒送",
  taobao_flash: "淘宝闪购 / 饿了么",
  jingdong: "京东秒送"
};

const TAKEOUT_PLATFORM_IDS: TakeoutPlatformId[] = [
  "meituan",
  "taobao_flash",
  "jingdong"
];

function platformName(platform: string): string {
  return PLATFORM_NAMES[platform] || platform || "未知平台";
}

function cloudPlatformMark(platform: TakeoutPlatformId): string {
  if (platform === "taobao_flash") return "淘";
  if (platform === "meituan") return "美";
  return "京";
}

function metricChannelLabel(channel: string): string {
  return {
    takeout: "外卖汇总",
    meituan_takeout: "美团外卖",
    eleme_takeout: "淘宝闪购",
    jd_takeout: "京东秒送",
    offline: "堂食",
    groupbuy: "团购",
    xiaohongshu: "小红书",
    shortvideo: "短视频"
  }[channel] || channel;
}

function promotionPolicyFor(platform: TakeoutPlatformId): PromotionPolicy {
  const defaults: Record<TakeoutPlatformId, Pick<PromotionPolicy, "dailyBudgetCap" | "bidCap" | "maxChangePercent" | "targetRoi" | "minSpendForEvaluation" | "stopLossRoi" | "observationDays" | "metricWindowDays" | "preferredTimeRange" | "notes">> = {
    meituan: {
      dailyBudgetCap: 120,
      bidCap: 3,
      maxChangePercent: 10,
      targetRoi: 2.5,
      minSpendForEvaluation: 30,
      stopLossRoi: 1.5,
      observationDays: 3,
      metricWindowDays: 7,
      preferredTimeRange: "近7天",
      notes: "美团优先控制推广花费，单次调整后至少观察 3 天。"
    },
    taobao_flash: {
      dailyBudgetCap: 100,
      bidCap: 2.5,
      maxChangePercent: 10,
      targetRoi: 2.3,
      minSpendForEvaluation: 25,
      stopLossRoi: 1.4,
      observationDays: 3,
      metricWindowDays: 7,
      preferredTimeRange: "近7天",
      notes: "淘宝闪购优先优化进店与下单转化。"
    },
    jingdong: {
      dailyBudgetCap: 80,
      bidCap: 2,
      maxChangePercent: 8,
      targetRoi: 2,
      minSpendForEvaluation: 20,
      stopLossRoi: 1.3,
      observationDays: 3,
      metricWindowDays: 7,
      preferredTimeRange: "近7天",
      notes: "京东秒送首版采用更保守的预算与出价上限。"
    }
  };
  const existing = state.promotionPolicies.find((policy) => policy.platform === platform);
  return (
    existing || {
      id: `default-${platform}`,
      organizationId: state.user?.organization.id || "",
      platform,
      platformName: platformName(platform),
      enabled: false,
      mode: "suggest",
      ...defaults[platform],
      requireCompleteData: true,
      isDefault: true
    }
  );
}

function platformMark(platform: string): string {
  if (platform === "eleme" || platform === "taobao_flash") return "淘";
  if (platform === "meituan") return "美";
  if (platform === "jd" || platform === "jingdong") return "京";
  return "平";
}

function platformResultTone(status: string): string {
  if (status === "completed") return "succeeded";
  if (status === "waiting_login" || status === "no_data" || status === "no_export_found") return "pending_approval";
  if (status === "failed") return "failed";
  return "running";
}

function platformResultLabel(status: string): string {
  if (status === "completed") return "采集完成";
  if (status === "waiting_login") return "等待登录";
  if (status === "no_data") return "暂无可入库数据";
  if (status === "no_export_found") return "未找到导出入口";
  if (status === "failed") return "采集失败";
  return "等待执行";
}

function promotionActionLabel(action: string): string {
  return {
    increase_budget: "提高预算",
    decrease_budget: "降低预算",
    increase_bid: "提高出价",
    decrease_bid: "降低出价",
    pause: "暂停推广",
    keep: "维持观察"
  }[action] || action;
}

function platformTaskOutput(task: OpsTask | null): string {
  if (!task) {
    return `
      <div class="platform-tool-empty">
        <span>${icon("scan-search")}</span>
        <div><strong>还没有发起三平台采集</strong><p>先在 Mac 打开并登录平台，再执行批量采集。</p></div>
      </div>
    `;
  }
  const result = task.result || {};
  const platformResults = Array.isArray(result.platforms)
    ? (result.platforms as Array<Record<string, unknown>>)
    : [];
  const analysis =
    typeof result.analysis === "string"
      ? result.analysis
      : result.analysis && typeof result.analysis === "object"
        ? JSON.stringify(result.analysis, null, 2)
        : "";
  const promotionSuggestions = Array.isArray(result.promotionSuggestions)
    ? (result.promotionSuggestions as Array<Record<string, unknown>>)
    : [];
  const taskMessage = typeof result.message === "string" ? result.message : "";
  const metricSync =
    result.metricSync && typeof result.metricSync === "object"
      ? (result.metricSync as Record<string, unknown>)
      : null;
  const metricSyncImported = Number(metricSync?.imported || 0);
  const metricSyncSkipped = Number(metricSync?.skipped || 0);
  const isOpenTask = task.taskType === "PLATFORM_OPEN_ALL";
  const terminal = ["succeeded", "failed", "cancelled"].includes(task.status);
  const statusText = task.errorMessage || taskMessage || statusLabel(task.status);
  return `
    <div class="platform-tool-output">
      <div class="platform-task-summary">
        <div>
          <span class="status-pill ${escapeHtml(task.status)}">${escapeHtml(statusLabel(task.status))}</span>
          <strong>${escapeHtml(task.title)}</strong>
        </div>
        <div class="platform-task-actions">
          <small>${escapeHtml(relativeTime(task.updatedAt))} · ${escapeHtml(task.id.slice(0, 8))}</small>
          <button class="text-button" id="refresh-platform-task">${icon("refresh-cw")} 刷新状态</button>
        </div>
      </div>
      <p class="platform-task-message">${escapeHtml(statusText)}</p>
      ${
        platformResults.length
          ? `
            <div class="platform-result-grid">
              ${platformResults
                .map((item) => {
                  const platform = String(item.platform || "");
                  const status = String(item.status || "pending");
                  const message = String(item.message || "等待执行");
                  const title = String(item.title || "");
                  const url = String(item.url || "");
                  return `
                    <article class="platform-result-card">
                      <span class="platform-result-mark">${escapeHtml(platformMark(platform))}</span>
                      <div>
                        <strong>${escapeHtml(platformName(platform))}</strong>
                        <p>${escapeHtml(message)}</p>
                        ${title ? `<small>${escapeHtml(title)}</small>` : ""}
                        ${url ? `<small>${escapeHtml(url)}</small>` : ""}
                      </div>
                      <span class="status-pill ${platformResultTone(status)}">${escapeHtml(platformResultLabel(status))}</span>
                    </article>
                  `;
                })
                .join("")}
            </div>
          `
          : ""
      }
      ${
        isOpenTask && task.status === "succeeded" && !platformResults.length
          ? `<div class="platform-open-result">${icon("external-link")} 三平台窗口已打开，请在 Mac 完成登录或验证码后执行采集。</div>`
          : ""
      }
      ${
        task.status === "local_confirmation_required"
          ? `<div class="platform-confirmation">${icon("shield-alert")} 任务正在等待 Mac 本地确认，请在执行端查看旧值、新值和执行步骤。</div>`
          : ""
      }
      ${
        metricSync
          ? `<div class="platform-sync-result ${metricSyncImported > 0 ? "success" : "muted"}">
              ${icon(metricSyncImported > 0 ? "database-zap" : "shield-check")}
              已同步入库 ${metricSyncImported} 条门店级指标；跳过 ${metricSyncSkipped} 条不可安全映射的数据。
            </div>`
          : ""
      }
      ${
        promotionSuggestions.length
          ? `
            <div class="promotion-plan-head">
              <strong>本轮推广调优建议</strong>
              <span>仅生成建议，实际修改仍需老板审批和 Mac 本机确认</span>
            </div>
            <div class="promotion-suggestion-list">
              ${promotionSuggestions
                .map((item) => {
                  const platform = String(item.platform || "");
                  const storeName = String(item.storeName || "未匹配门店");
                  const action = String(item.action || "keep");
                  const riskLevel = String(item.riskLevel || "high");
                  const currentBudget = Number(item.currentBudget || 0);
                  const suggestedBudget = Number(item.suggestedBudget || 0);
                  const currentBid = Number(item.currentBid || 0);
                  const suggestedBid = Number(item.suggestedBid || 0);
                  const reason = String(item.reason || "");
                  const evidence = String(item.evidence || "");
                  const rollback = String(item.rollback || "");
                  return `
                    <article class="promotion-suggestion">
                      <div class="promotion-suggestion-head">
                        <span class="platform-result-mark">${escapeHtml(platformMark(platform))}</span>
                        <div><strong>${escapeHtml(storeName)}</strong><small>${escapeHtml(platformName(platform))}</small></div>
                        <span class="risk-tag ${escapeHtml(riskLevel)}">${escapeHtml(riskLabel(riskLevel as TaskRiskLevel))}</span>
                      </div>
                      <div class="promotion-change-row">
                        <span>${escapeHtml(promotionActionLabel(action))}</span>
                        <strong>预算 ${money(currentBudget)} → ${money(suggestedBudget)}</strong>
                        <strong>出价 ${money(currentBid)} → ${money(suggestedBid)}</strong>
                      </div>
                      <p>${escapeHtml(reason)}</p>
                      ${evidence ? `<small>依据：${escapeHtml(evidence)}</small>` : ""}
                      ${rollback ? `<small>回滚：${escapeHtml(rollback)}</small>` : ""}
                    </article>
                  `;
                })
                .join("")}
            </div>
          `
          : ""
      }
      ${
        !terminal && !platformResults.length && task.status !== "local_confirmation_required"
          ? `<div class="platform-progress">${icon("loader-circle")} 任务已进入执行队列，页面会自动更新结果。</div>`
          : ""
      }
      ${
        analysis
          ? `
            <div class="platform-analysis-head">
              <strong>本机 Codex 汇总</strong>
              <span>基于当前已登录页面证据，不补造缺失指标</span>
            </div>
            <pre class="platform-analysis">${escapeHtml(analysis)}</pre>
          `
          : ""
      }
    </div>
  `;
}

function renderPage(): void {
  const content = document.querySelector("#page-content");
  if (!content) return;
  const templates: Record<PageId, () => string> = {
    dashboard: dashboardTemplate,
    agent: agentTemplate,
    stores: storesTemplate,
    operations: operationsTemplate,
    data: dataTemplate,
    content: contentTemplate,
    media: mediaTemplate,
    tasks: tasksTemplate,
    devices: devicesTemplate,
    subscription: subscriptionTemplate,
    settings: settingsTemplate
  };
  content.innerHTML = templates[state.page]();
  wireNavigationButtons();
  wirePage();
  if (state.page === "dashboard") renderChart();
}

function renderChart(): void {
  const element = document.querySelector<HTMLElement>("#dashboard-chart");
  const trend = state.dashboard?.trend || [];
  if (!element) return;
  state.chart?.dispose();
  state.chart = echarts.init(element);
  state.chart.setOption({
    animationDuration: 700,
    tooltip: { trigger: "axis" },
    legend: { top: 0, right: 0, data: ["营收", "订单"], textStyle: { color: "#5d6877" } },
    grid: { left: 8, right: 8, top: 42, bottom: 6, containLabel: true },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: trend.map((item) => item.date.slice(5)),
      axisLine: { lineStyle: { color: "#d9dfe7" } },
      axisLabel: { color: "#6c7787" }
    },
    yAxis: [
      {
        type: "value",
        splitLine: { lineStyle: { color: "#edf0f4" } },
        axisLabel: { color: "#6c7787", formatter: (value: number) => (value >= 10000 ? `${value / 10000}万` : value) }
      },
      { type: "value", show: false }
    ],
    series: [
      {
        name: "营收",
        type: "line",
        smooth: true,
        symbolSize: 7,
        data: trend.map((item) => item.revenue),
        areaStyle: { color: "rgba(14, 116, 144, .12)" },
        lineStyle: { color: "#0e7490", width: 3 },
        itemStyle: { color: "#0e7490" }
      },
      {
        name: "订单",
        type: "bar",
        yAxisIndex: 1,
        barWidth: 10,
        data: trend.map((item) => item.orders),
        itemStyle: { color: "rgba(245, 158, 11, .55)", borderRadius: [4, 4, 0, 0] }
      }
    ]
  });
}

function wirePage(): void {
  if (state.page === "agent") wireAgent();
  if (state.page === "stores") wireStores();
  if (state.page === "operations") wireOperations();
  if (state.page === "data") wireData();
  if (state.page === "content") wireContent();
  if (state.page === "media") wireMedia();
  if (state.page === "tasks") wireTasks();
  if (state.page === "devices") wireDevices();
  if (state.page === "subscription") wireSubscription();
  if (state.page === "settings") wireSettings();
}

async function launchPlatformTool(
  taskType: "PLATFORM_OPEN_ALL" | "PLATFORM_COLLECT_ALL" | "PLATFORM_PROMOTION_PLAN_ALL"
): Promise<void> {
  const busy =
    taskType === "PLATFORM_OPEN_ALL"
      ? "open"
      : taskType === "PLATFORM_COLLECT_ALL"
        ? "collect"
        : "promotion";
  const title =
    taskType === "PLATFORM_OPEN_ALL"
      ? "打开并登录三平台"
      : taskType === "PLATFORM_COLLECT_ALL"
        ? "一键采集三平台"
        : "三平台推广自动调优";
  const detail =
    taskType === "PLATFORM_OPEN_ALL"
      ? "在 Mac 打开三平台受控浏览器并尝试自动填充已保存账号，验证码或扫码仍需人工完成。"
      : taskType === "PLATFORM_COLLECT_ALL"
        ? "逐店读取当前已登录的美团、淘宝闪购和京东门店数据（订单、营业额、访客），不执行任何平台写入。"
        : "读取三平台当前推广数据，由本机 Codex 生成预算、出价和开关调优建议；不直接修改平台。";
  const storeMappings = (state.dashboard?.stores || []).map((store) => ({
    id: store.id,
    name: store.name,
    city: store.city,
    owner: store.owner,
    status: store.status,
    platformIds: {
      eleme: store.platformIds?.taobao_flash || "",
      meituan: store.platformIds?.meituan || "",
      jd: store.platformIds?.jingdong || ""
    },
    displayName: {
      eleme: store.accountNames?.taobao_flash || store.name,
      meituan: store.accountNames?.meituan || store.name,
      jd: store.accountNames?.jingdong || store.name
    },
    metrics: {
      revenue: Number(store.revenue || 0),
      orders: Number(store.orders || 0),
      visitors: Number(store.visitors || 0),
      conversion: Number(store.conversion || 0)
    }
  }));
  if (taskType !== "PLATFORM_OPEN_ALL" && storeMappings.length === 0) {
    toast("门店数据尚未加载，请稍后重试", "error");
    return;
  }
  state.platformToolBusy = busy;
  renderPage();
  refreshIcons();
  try {
    const task = await createTask({
      title,
      taskType,
      kind: "read",
      riskLevel: "low",
      status: "approved",
      priority: "P1",
      detail,
      payload: {
        platforms: ["eleme", "meituan", "jd"],
        storeIds: [],
        storeMappings,
        promotionPolicies: state.promotionPolicies
      }
    });
    sessionStorage.setItem("boss-platform-task-id", task.id);
    state.platformTask = task;
    state.tasks = [task, ...state.tasks.filter((item) => item.id !== task.id)];
    state.platformToolBusy = "";
    renderPage();
    refreshIcons();
    toast(
      taskType === "PLATFORM_OPEN_ALL"
        ? "登录任务已发送到 Mac 执行端"
        : taskType === "PLATFORM_COLLECT_ALL"
          ? "三平台采集任务已发送"
          : "推广调优任务已发送"
    );
    startPlatformPolling(task.id);
  } catch (error) {
    state.platformToolBusy = "";
    renderPage();
    refreshIcons();
    toast(error instanceof Error ? error.message : "任务创建失败", "error");
  }
}

function startPlatformPolling(taskId: string): void {
  if (!taskId || state.platformPollTaskId === taskId) return;
  state.platformPollTaskId = taskId;
  void pollPlatformTask(taskId, 0);
}

function applyPlatformTask(task: OpsTask): void {
  state.platformTask = task;
  state.tasks = state.tasks.map((item) => (item.id === task.id ? task : item));
  sessionStorage.setItem("boss-platform-task-id", task.id);
}

async function pollPlatformTask(taskId: string, attempt = 0): Promise<void> {
  if (state.platformTask?.id !== taskId || attempt >= 600) {
    if (state.platformPollTaskId === taskId) state.platformPollTaskId = "";
    if (attempt >= 600 && state.platformTask?.id === taskId) {
      toast("三平台任务仍在本机执行，可稍后在任务与审批中查看。", "error");
    }
    return;
  }
  await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 1600 : 3000));
  try {
    const task = await getTask(taskId);
    if (state.platformTask?.id !== taskId) return;
    applyPlatformTask(task);
    if (state.page === "operations") {
      renderPage();
      refreshIcons();
    }
    if (task.status === "succeeded") {
      state.platformPollTaskId = "";
      const metricSync =
        task.result?.metricSync && typeof task.result.metricSync === "object"
          ? (task.result.metricSync as Record<string, unknown>)
          : null;
      const imported = Number(metricSync?.imported || 0);
      if (task.taskType === "PLATFORM_COLLECT_ALL" && imported > 0) {
        state.dashboard = await getDashboard().catch(() => state.dashboard);
        state.tasks = await listTasks().catch(() => state.tasks);
        if (state.page === "dashboard" || state.page === "operations") {
          renderPage();
          refreshIcons();
        }
      }
      if (task.taskType === "PLATFORM_COLLECT_ALL" && imported === 0) {
        toast("采集完成，但没有可安全入库的门店数据，请检查是否进入单店经营后台", "warning");
      } else if (task.taskType === "PLATFORM_OPEN_ALL") {
        toast("三平台浏览器已打开");
      } else if (task.taskType === "PLATFORM_COLLECT_ALL") {
        toast(`三平台采集完成，已同步入库 ${imported} 条`);
      } else {
        toast("推广调优方案已生成");
      }
      return;
    }
    if (task.status === "failed" || task.status === "cancelled") {
      state.platformPollTaskId = "";
      toast(task.errorMessage || "三平台任务未完成", "error");
      return;
    }
    void pollPlatformTask(taskId, attempt + 1);
  } catch {
    void pollPlatformTask(taskId, attempt + 1);
  }
}

function openPromotionPolicyDialog(platform: TakeoutPlatformId): void {
  const policy = promotionPolicyFor(platform);
  const dialog = document.createElement("dialog");
  dialog.className = "modal";
  dialog.innerHTML = `
    <form class="modal-card promotion-policy-form" id="promotion-policy-form">
      <div class="panel-head">
        <div><h2>${escapeHtml(policy.platformName)}自动推广设置</h2><span>系统只生成预算和出价动作，平台写入必须审批与本机确认</span></div>
        <button class="icon-button" type="button" data-close-policy aria-label="关闭">${icon("x")}</button>
      </div>
      <div class="promotion-policy-summary">
        <span>${icon("shield-check")} 建议模式不会自动改平台</span>
        <span>${icon("activity")} 数据不足、花费过低或低于止损线时保持不动</span>
      </div>
      <div class="form-grid">
        <label><span>运行模式</span><select name="mode">
          <option value="suggest" ${policy.mode === "suggest" ? "selected" : ""}>只生成建议</option>
          <option value="guarded_auto" ${policy.mode === "guarded_auto" ? "selected" : ""}>受控自动计算</option>
        </select></label>
        <label><span>自动评估</span><select name="enabled">
          <option value="false" ${policy.enabled ? "" : "selected"}>关闭</option>
          <option value="true" ${policy.enabled ? "selected" : ""}>开启</option>
        </select></label>
        <label><span>单店日预算上限</span><input name="dailyBudgetCap" type="number" min="0" step="1" value="${policy.dailyBudgetCap}" required /></label>
        <label><span>最高出价</span><input name="bidCap" type="number" min="0" step="0.1" value="${policy.bidCap}" required /></label>
        <label><span>单次最大调整比例</span><input name="maxChangePercent" type="number" min="0" max="50" step="1" value="${policy.maxChangePercent}" required /></label>
        <label><span>目标 ROI</span><input name="targetRoi" type="number" min="0" step="0.1" value="${policy.targetRoi}" required /></label>
        <label><span>最低评估花费</span><input name="minSpendForEvaluation" type="number" min="0" step="1" value="${policy.minSpendForEvaluation}" required /></label>
        <label><span>止损 ROI</span><input name="stopLossRoi" type="number" min="0" step="0.1" value="${policy.stopLossRoi}" required /></label>
        <label><span>观察期（天）</span><input name="observationDays" type="number" min="1" max="30" step="1" value="${policy.observationDays}" required /></label>
        <label><span>采集数据窗口（天）</span><input name="metricWindowDays" type="number" min="1" max="30" step="1" value="${policy.metricWindowDays}" required /></label>
        <label><span>推荐采集时间段</span><input name="preferredTimeRange" value="${escapeHtml(policy.preferredTimeRange)}" required /></label>
        <label><span>数据完整性</span><select name="requireCompleteData">
          <option value="true" ${policy.requireCompleteData ? "selected" : ""}>缺少核心数据不动作</option>
          <option value="false" ${policy.requireCompleteData ? "" : "selected"}>允许使用不完整数据评估</option>
        </select></label>
      </div>
      <label><span>平台标准说明</span><textarea name="notes" rows="3">${escapeHtml(policy.notes)}</textarea></label>
      <div class="binding-boundary">${icon("shield-alert")} “受控自动计算”只代表系统可以自动算建议，并不绕过老板审批和 Mac 本机确认。</div>
      <div class="modal-actions">
        <button class="secondary-button" type="button" data-close-policy>取消</button>
        <button class="primary-button" type="submit">${icon("save")} 保存推广标准</button>
      </div>
    </form>
  `;
  document.body.append(dialog);
  refreshIcons();
  dialog.showModal();
  dialog.querySelectorAll("[data-close-policy]").forEach((button) => {
    button.addEventListener("click", () => dialog.close());
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.querySelector<HTMLFormElement>("#promotion-policy-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const form = new FormData(formElement);
    const submit = formElement.querySelector<HTMLButtonElement>("button[type=submit]");
    if (submit) submit.disabled = true;
    try {
      const saved = await savePromotionPolicy({
        platform,
        enabled: String(form.get("enabled")) === "true",
        mode: String(form.get("mode")) === "guarded_auto" ? "guarded_auto" : "suggest",
        dailyBudgetCap: Number(form.get("dailyBudgetCap") || 0),
        bidCap: Number(form.get("bidCap") || 0),
        maxChangePercent: Number(form.get("maxChangePercent") || 0),
        targetRoi: Number(form.get("targetRoi") || 0),
        minSpendForEvaluation: Number(form.get("minSpendForEvaluation") || 0),
        stopLossRoi: Number(form.get("stopLossRoi") || 0),
        observationDays: Number(form.get("observationDays") || 3),
        metricWindowDays: Number(form.get("metricWindowDays") || 7),
        preferredTimeRange: String(form.get("preferredTimeRange") || "近7天"),
        requireCompleteData: String(form.get("requireCompleteData")) !== "false",
        notes: String(form.get("notes") || "")
      });
      state.promotionPolicies = [
        saved,
        ...state.promotionPolicies.filter((item) => item.platform !== saved.platform)
      ];
      dialog.close();
      renderPage();
      refreshIcons();
      toast(`${saved.platformName}推广标准已保存`);
    } catch (error) {
      toast(error instanceof Error ? error.message : "保存失败", "error");
      if (submit) submit.disabled = false;
    }
  });
  dialog.addEventListener("close", () => dialog.remove());
}

function wireOperations(): void {
  const platformUrls: Record<string, string> = {
    meituan: "https://e.waimai.meituan.com/",
    taobao_flash: "https://melody.shop.ele.me/",
    jingdong: "https://store.jddj.com/"
  };
  document.querySelector("#open-platforms-button")?.addEventListener("click", () => {
    void launchPlatformTool("PLATFORM_OPEN_ALL");
  });
  document.querySelector("#collect-platforms-button")?.addEventListener("click", () => {
    void launchPlatformTool("PLATFORM_COLLECT_ALL");
  });
  document.querySelector("#collect-ad-spend-button")?.addEventListener("click", async () => {
    const button = document.querySelector<HTMLButtonElement>("#collect-ad-spend-button");
    if (button) button.disabled = true;
    try {
      const storeMappings = (state.dashboard?.stores || []).map((store) => ({
        id: store.id,
        name: store.name,
        platformIds: {
          eleme: store.platformIds?.taobao_flash || "",
          meituan: store.platformIds?.meituan || "",
          jd: store.platformIds?.jingdong || ""
        },
        displayName: {
          eleme: store.accountNames?.taobao_flash || store.name,
          meituan: store.accountNames?.meituan || store.name,
          jd: store.accountNames?.jingdong || store.name
        }
      }));
      const task = await createTask({
        title: "采集三平台推广消耗",
        taskType: "PLATFORM_AD_SPEND",
        kind: "read",
        riskLevel: "low",
        status: "approved",
        priority: "P1",
        detail: "通过 Edge 只读进入三平台推广数据中心，提取今日推广消费；不执行任何平台写入。",
        payload: {
          platforms: ["eleme", "meituan", "jd"],
          storeMappings
        }
      });
      state.tasks = [task, ...state.tasks.filter((item) => item.id !== task.id)];
      toast("推广消耗采集任务已发送到 Mac 执行端");
      navigate("tasks");
    } catch (error) {
      toast(error instanceof Error ? error.message : "创建失败", "error");
    } finally {
      if (button) button.disabled = false;
    }
  });
  document.querySelector("#open-edge-login-button")?.addEventListener("click", async () => {
    try {
      const task = await createTask({
        title: "打开 Edge 自动化登录",
        taskType: "EDGE_OPEN_LOGIN",
        kind: "read",
        riskLevel: "low",
        status: "approved",
        priority: "P2",
        detail: "打开 Edge 自动化实例，用于登录美团、淘宝闪购和京东。",
        payload: { platforms: ["eleme", "meituan", "jd"] }
      });
      state.tasks = [task, ...state.tasks.filter((item) => item.id !== task.id)];
      toast("Edge 登录任务已发送");
    } catch (error) {
      toast(error instanceof Error ? error.message : "创建失败", "error");
    }
  });
  document.querySelector("#promotion-plan-button")?.addEventListener("click", () => {
    void launchPlatformTool("PLATFORM_PROMOTION_PLAN_ALL");
  });
  document.querySelector("#create-store-audit-tasks")?.addEventListener("click", async () => {
    const stores = state.dashboard?.stores || [];
    const boundCount = stores.reduce(
      (sum, store) =>
        sum + TAKEOUT_PLATFORM_IDS.filter((platform) => store.platformIds?.[platform]).length,
      0
    );
    if (!window.confirm(`将为 ${stores.length} 家门店创建 ${boundCount} 个逐店只读采集任务（已跳过未绑定平台）。任务会串行领取，可能需要较长时间，是否继续？`)) return;
    const button = document.querySelector<HTMLButtonElement>("#create-store-audit-tasks");
    if (button) button.disabled = true;
    try {
      for (const store of stores) {
        const mapping = {
          id: store.id,
          name: store.name,
          city: store.city,
          owner: store.owner,
          status: store.status,
          platformIds: {
            eleme: store.platformIds?.taobao_flash || "",
            meituan: store.platformIds?.meituan || "",
            jd: store.platformIds?.jingdong || ""
          },
          displayName: {
            eleme: store.accountNames?.taobao_flash || store.name,
            meituan: store.accountNames?.meituan || store.name,
            jd: store.accountNames?.jingdong || store.name
          }
        };
        for (const platform of TAKEOUT_PLATFORM_IDS) {
          if (!store.platformIds?.[platform]) continue;
          await createTask({
            title: `${store.name}${platformName(platform)}逐店采集`,
            taskType: "PLATFORM_STORE_COLLECT",
            kind: "read",
            riskLevel: "low",
            status: "approved",
            priority: "P2",
            storeId: store.id,
            platform,
            detail: "本机 Codex 只读切换至该门店平台后台并采集当前页面；不执行任何平台写入。",
            payload: {
              platforms: [platform],
              storeIds: [store.id],
              storeMappings: [mapping]
            }
          });
        }
      }
      state.tasks = await listTasks();
      toast(`已创建 ${boundCount} 个逐店采集任务`);
      navigate("tasks");
    } catch (error) {
      toast(error instanceof Error ? error.message : "创建失败", "error");
      if (button) button.disabled = false;
    }
  });
  document.querySelector("#export-platform-reports")?.addEventListener("click", async () => {
    const button = document.querySelector<HTMLButtonElement>("#export-platform-reports");
    if (button) button.disabled = true;
    try {
      const task = await createTask({
        title: "自动导出三平台报表",
        taskType: "PLATFORM_EXPORT_ALL",
        kind: "read",
        riskLevel: "low",
        status: "approved",
        priority: "P1",
        detail: "在已登录的三平台页面尝试点击导出/下载入口；下载文件进入平台报表文件夹并自动入库。"
      });
      state.platformTask = task;
      state.tasks = [task, ...state.tasks.filter((item) => item.id !== task.id)];
      renderPage();
      refreshIcons();
      startPlatformPolling(task.id);
      toast("自动导出任务已发送");
    } catch (error) {
      toast(error instanceof Error ? error.message : "创建失败", "error");
      if (button) button.disabled = false;
    }
  });
  document.querySelectorAll<HTMLButtonElement>("[data-promotion-settings]").forEach((button) => {
    button.addEventListener("click", () => {
      openPromotionPolicyDialog(button.dataset.promotionSettings as TakeoutPlatformId);
    });
  });
  document.querySelector("#generate-promotion-plans")?.addEventListener("click", async (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    if (!window.confirm("将为全部门店生成或更新美团、淘宝闪购、京东三平台推广计划，是否继续？")) return;
    button.disabled = true;
    try {
      const result = await generatePromotionPlans();
      state.takeoutPlans = await listTakeoutPlans({ planType: "promotion" });
      renderPage();
      refreshIcons();
      toast(`推广计划已生成：新建 ${result.created}，更新 ${result.updated}`);
    } catch (error) {
      toast(error instanceof Error ? error.message : "生成失败", "error");
      button.disabled = false;
    }
  });
  document.querySelectorAll<HTMLButtonElement>("[data-plan-status]").forEach((button) => {
    button.addEventListener("click", async () => {
      const planId = button.dataset.planId || "";
      const status = button.dataset.planStatus as TakeoutPlan["status"];
      button.disabled = true;
      try {
        const plan = await updateTakeoutPlanStatus(planId, status);
        state.takeoutPlans = state.takeoutPlans.map((item) => (item.id === plan.id ? plan : item));
        renderPage();
        refreshIcons();
        toast("推广计划状态已更新");
      } catch (error) {
        toast(error instanceof Error ? error.message : "更新失败", "error");
        button.disabled = false;
      }
    });
  });
  document.querySelector("#refresh-platform-task")?.addEventListener("click", async () => {
    const taskId = state.platformTask?.id;
    if (!taskId) return;
    const button = document.querySelector<HTMLButtonElement>("#refresh-platform-task");
    if (button) button.disabled = true;
    try {
      const task = await getTask(taskId);
      applyPlatformTask(task);
      renderPage();
      refreshIcons();
      if (!["succeeded", "failed", "cancelled"].includes(task.status)) {
        startPlatformPolling(task.id);
        toast("状态已刷新，任务仍在执行中");
      } else {
        toast("任务状态已刷新");
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : "刷新失败", "error");
      if (button) button.disabled = false;
    }
  });
  if (state.platformTask && !["succeeded", "failed", "cancelled"].includes(state.platformTask.status)) {
    startPlatformPolling(state.platformTask.id);
  }
  document.querySelectorAll<HTMLButtonElement>("[data-open-integration]").forEach((button) => {
    button.addEventListener("click", () => {
      const url = platformUrls[button.dataset.openIntegration || ""];
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-create-platform-task]").forEach((button) => {
    button.addEventListener("click", async () => {
      const platform = button.dataset.createPlatformTask || "";
      const names: Record<string, string> = {
        meituan: "美团外卖",
        taobao_flash: "淘宝闪购 / 饿了么",
        jingdong: "京东秒送"
      };
      button.disabled = true;
      try {
        const task = await createTask({
          title: `${names[platform] || platform}运营检查`,
          taskType: "TASK",
          kind: "read",
          riskLevel: "low",
          status: "approved",
          priority: "P2",
          platform: platform as OpsTask["platform"],
          detail: "读取当前门店商品、活动、评价、推广和履约数据，只生成整改建议，不执行平台写入。"
        });
        state.tasks = [task, ...state.tasks];
        toast("运营检查任务已创建");
        navigate("tasks");
      } catch (error) {
        toast(error instanceof Error ? error.message : "任务创建失败", "error");
        button.disabled = false;
      }
    });
  });
}

function wireAgent(): void {
  document.querySelector<HTMLFormElement>("#period-report-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget as HTMLFormElement);
    const periodDays = Number(form.get("periodDays") || 15);
    const output = document.querySelector<HTMLDivElement>("#period-report-output");
    const submit = (event.currentTarget as HTMLFormElement).querySelector<HTMLButtonElement>("button[type=submit]");
    if (output) output.innerHTML = `<div class="loading-state">正在生成报告…</div>`;
    if (submit) submit.disabled = true;
    try {
      const report = await getPeriodReport(periodDays);
      state.periodReport = report.markdown;
      if (output) output.innerHTML = `<div class="period-report-markdown">${renderMarkdown(report.markdown)}</div>`;
    } catch (error) {
      if (output) output.innerHTML = `<div class="form-error">${escapeHtml(error instanceof Error ? error.message : "生成失败")}</div>`;
    } finally {
      if (submit) submit.disabled = false;
    }
  });
  document.querySelector<HTMLFormElement>("#codex-instruction-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const form = new FormData(formElement);
    const mode = String(form.get("mode") || "read");
    const instruction = String(form.get("instruction") || "").trim();
    if (!instruction) return toast("请填写 Codex 指令", "error");
    const platform = (String(form.get("platform") || "") || undefined) as OpsTask["platform"] | undefined;
    const storeId = String(form.get("storeId") || "") || undefined;
    const submit = formElement.querySelector<HTMLButtonElement>("button[type=submit]");
    if (submit) submit.disabled = true;
    try {
      const task = await createTask({
        title: mode === "browser_action" ? "本机 Codex 浏览器动作" : "本机 Codex 指令",
        taskType: mode === "browser_action" ? "CODEX_BROWSER_ACTION" : "CODEX_INSTRUCTION",
        kind: "read",
        riskLevel: "low",
        status: "approved",
        priority: "P1",
        storeId,
        platform,
        detail: instruction,
        payload: {
          instruction,
          ...(mode === "browser_action" ? { requiresLocalConfirmation: true } : {})
        }
      });
      state.tasks = [task, ...state.tasks.filter((item) => item.id !== task.id)];
      toast("Codex 指令已绑定并发送到 Mac 执行端");
      navigate("tasks");
    } catch (error) {
      toast(error instanceof Error ? error.message : "执行失败", "error");
      if (submit) submit.disabled = false;
    }
  });
  document.querySelector<HTMLFormElement>("#agent-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const form = new FormData(formElement);
    const select = formElement.querySelector<HTMLSelectElement>("select[name=storeIds]");
    const storeIds = select ? [...select.selectedOptions].map((option) => option.value).filter(Boolean) : [];
    const button = formElement.querySelector<HTMLButtonElement>("button[type=submit]");
    if (button) {
      button.disabled = true;
      button.innerHTML = `${icon("loader-circle")} 正在分析`;
      refreshIcons();
    }
    try {
      state.agentAnswer = await askBossAgent(
        String(form.get("prompt") || ""),
        storeIds,
        Number(form.get("periodDays") || 7)
      );
      renderPage();
      refreshIcons();
      if (state.agentAnswer.pending && state.agentAnswer.runId) {
        void pollLocalAgentAnswer(state.agentAnswer.runId, state.agentAnswer);
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : "分析失败", "error");
      if (button) {
        button.disabled = false;
        button.innerHTML = `${icon("send")} 开始分析`;
        refreshIcons();
      }
    }
  });
  document.querySelector("#convert-actions")?.addEventListener("click", async () => {
    const answer = state.agentAnswer;
    if (!answer) return;
    const selected = [...document.querySelectorAll<HTMLInputElement>("[data-agent-action]:checked")]
      .map((input) => answer.recommendedActions[Number(input.dataset.agentAction)])
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
    if (!selected.length) return toast("请至少选择一项建议动作", "error");
    try {
      for (const action of selected) {
        await createTask({
          title: action.title,
          taskType: action.kind === "write" ? "平台操作" : action.kind === "media" ? "媒体任务" : "经营分析",
          detail: action.detail,
          kind: action.kind,
          riskLevel: action.riskLevel,
          storeId: action.storeId,
          platform: action.platform,
          status: action.requiresApproval ? "pending_approval" : "approved",
          priority: action.riskLevel === "high" ? "P0" : "P1"
        });
      }
      state.tasks = await listTasks();
      toast(`已创建 ${selected.length} 个任务`);
      navigate("tasks");
    } catch (error) {
      toast(error instanceof Error ? error.message : "任务创建失败", "error");
    }
  });
}

async function pollLocalAgentAnswer(
  runId: string,
  fallback: BossAgentAnswer,
  attempt = 0
): Promise<void> {
  if (attempt >= 40) {
    toast("本机 Codex 分析仍在执行，可稍后在任务中心查看。", "error");
    return;
  }
  await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 1500 : 3000));
  try {
    const task = await getTask(runId);
    if (task.status === "succeeded") {
      const resultPayload = task.result as Record<string, unknown> | undefined;
      const nestedResult = resultPayload?.result as Record<string, unknown> | undefined;
      const content =
        typeof resultPayload?.content === "string"
          ? resultPayload.content
          : typeof nestedResult?.content === "string"
            ? nestedResult.content
          : "";
      state.agentAnswer = {
        ...fallback,
        source: "local_codex",
        pending: false,
        runId,
        summary: content || fallback.summary
      };
      if (state.page === "agent") {
        renderPage();
        refreshIcons();
      }
      toast("本机 Codex 分析完成");
      return;
    }
    if (task.status === "failed" || task.status === "cancelled") {
      state.agentAnswer = { ...fallback, source: "data_only", pending: false, runId };
      if (state.page === "agent") {
        renderPage();
        refreshIcons();
      }
      toast(task.errorMessage || "本机 Codex 分析失败", "error");
      return;
    }
    void pollLocalAgentAnswer(runId, fallback, attempt + 1);
  } catch {
    void pollLocalAgentAnswer(runId, fallback, attempt + 1);
  }
}

function takeoutAccountFor(storeId: string, platform: TakeoutPlatformId): TakeoutAccount | undefined {
  return state.takeoutAccounts.find(
    (account) => account.storeId === storeId && account.platform === platform
  );
}

function openStorePlatformDialog(storeId: string): void {
  const store = state.dashboard?.stores.find((item) => item.id === storeId);
  if (!store) return;
  const dialog = document.createElement("dialog");
  dialog.className = "modal";
  dialog.innerHTML = `
    <div class="modal-card platform-binding-modal">
      <div class="panel-head">
        <div><h2>${escapeHtml(store.name)} · 平台后台绑定</h2><span>平台密码仍只保存在 Mac 执行端钥匙串，云端仅保存账号名称和门店 ID</span></div>
        <button class="icon-button" type="button" data-close-binding aria-label="关闭">${icon("x")}</button>
      </div>
      <div class="binding-audit">
        <span class="${TAKEOUT_PLATFORM_IDS.every((platform) => takeoutAccountFor(store.id, platform)) ? "ready" : "pending"}">
          ${TAKEOUT_PLATFORM_IDS.filter((platform) => takeoutAccountFor(store.id, platform)).length}/3 个平台已绑定
        </span>
        <small>审查门店时必须完成美团、淘宝闪购、京东秒送三个后台映射。</small>
      </div>
      <div class="platform-binding-list">
        ${TAKEOUT_PLATFORM_IDS.map((platform) => {
          const account = takeoutAccountFor(store.id, platform);
          const fallbackId = store.platformIds?.[platform] || "";
          return `
            <article class="platform-binding-row">
              <span class="platform-result-mark">${cloudPlatformMark(platform)}</span>
              <div>
                <strong>${escapeHtml(platformName(platform))}</strong>
                <p>${account ? escapeHtml(account.accountName) : fallbackId ? "已导入平台ID，资料待补全" : "尚未绑定平台后台"}</p>
                <small>${escapeHtml(account?.externalStoreId || fallbackId || "缺少平台门店ID")}</small>
              </div>
              <span class="status-pill ${account?.status === "active" ? "ready" : account ? "pending_approval" : "failed"}">
                ${account ? (account.status === "active" ? "启用中" : "已停用") : "未绑定"}
              </span>
              <button class="secondary-button compact" type="button" data-edit-platform-binding data-store-id="${escapeHtml(store.id)}" data-platform="${platform}">
                ${account ? "编辑绑定" : "添加绑定"}
              </button>
            </article>
          `;
        }).join("")}
      </div>
    </div>
  `;
  document.body.append(dialog);
  refreshIcons();
  dialog.showModal();
  dialog.querySelector("[data-close-binding]")?.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.querySelectorAll<HTMLButtonElement>("[data-edit-platform-binding]").forEach((button) => {
    button.addEventListener("click", () => {
      const targetStoreId = button.dataset.storeId || "";
      const platform = button.dataset.platform as TakeoutPlatformId;
      dialog.close();
      openPlatformBindingForm(targetStoreId, platform);
    });
  });
  dialog.addEventListener("close", () => dialog.remove());
}

function openPlatformBindingForm(
  storeId?: string,
  platform?: TakeoutPlatformId
): void {
  const stores = state.dashboard?.stores || [];
  if (!stores.length) return toast("还没有可绑定的门店", "error");
  const selectedStoreId = storeId || stores[0]!.id;
  const selectedPlatform = platform || TAKEOUT_PLATFORM_IDS[0]!;
  let existing = takeoutAccountFor(selectedStoreId, selectedPlatform);
  let activeStoreId = selectedStoreId;
  let activePlatform = selectedPlatform;
  const store = stores.find((item) => item.id === selectedStoreId)!;
  const dialog = document.createElement("dialog");
  dialog.className = "modal";
  dialog.innerHTML = `
    <form class="modal-card platform-binding-form" id="platform-binding-form">
      <div class="panel-head">
        <div><h2>${existing ? "编辑平台绑定" : "添加平台绑定"}</h2><span>一个门店同一平台保留一条有效后台映射</span></div>
        <button class="icon-button" type="button" data-close-binding aria-label="关闭">${icon("x")}</button>
      </div>
      <div class="form-grid">
        <label><span>门店</span><select name="storeId" ${storeId ? "disabled" : ""}>
          ${stores.map((item) => `<option value="${escapeHtml(item.id)}" ${item.id === selectedStoreId ? "selected" : ""}>${escapeHtml(item.name)}</option>`).join("")}
        </select></label>
        <label><span>平台</span><select name="platform" ${platform ? "disabled" : ""}>
          ${TAKEOUT_PLATFORM_IDS.map((item) => `<option value="${item}" ${item === selectedPlatform ? "selected" : ""}>${escapeHtml(platformName(item))}</option>`).join("")}
        </select></label>
        <label><span>平台账号名称</span><input name="accountName" required value="${escapeHtml(existing?.accountName || `${store.name}-${platformName(selectedPlatform)}`)}" /></label>
        <label><span>平台门店 ID</span><input name="externalStoreId" required value="${escapeHtml(existing?.externalStoreId || store.platformIds?.[selectedPlatform] || "")}" /></label>
        <label><span>状态</span><select name="status"><option value="active" ${existing?.status !== "inactive" ? "selected" : ""}>启用</option><option value="inactive" ${existing?.status === "inactive" ? "selected" : ""}>停用</option></select></label>
      </div>
      <label><span>备注</span><textarea name="notes" rows="3">${escapeHtml(existing?.notes || "")}</textarea></label>
      <div class="binding-boundary">${icon("shield-check")} 这里不保存平台密码。密码、登录会话和验证码只在 Mac 执行端处理。</div>
      <div class="modal-actions">
        <button class="secondary-button" type="button" data-close-binding>取消</button>
        <button class="primary-button" type="submit">${icon("save")} 保存绑定</button>
      </div>
    </form>
  `;
  document.body.append(dialog);
  refreshIcons();
  dialog.showModal();
  const storeSelect = dialog.querySelector<HTMLSelectElement>('select[name="storeId"]');
  const platformSelect = dialog.querySelector<HTMLSelectElement>('select[name="platform"]');
  const accountNameInput = dialog.querySelector<HTMLInputElement>('input[name="accountName"]');
  const externalStoreIdInput = dialog.querySelector<HTMLInputElement>('input[name="externalStoreId"]');
  const statusSelect = dialog.querySelector<HTMLSelectElement>('select[name="status"]');
  const notesInput = dialog.querySelector<HTMLTextAreaElement>('textarea[name="notes"]');
  const titleElement = dialog.querySelector<HTMLHeadingElement>(".panel-head h2");
  const refreshBindingFields = () => {
    const activeStore = stores.find((item) => item.id === activeStoreId) || stores[0]!;
    existing = takeoutAccountFor(activeStore.id, activePlatform);
    if (accountNameInput) {
      accountNameInput.value =
        existing?.accountName || `${activeStore.name}-${platformName(activePlatform)}`;
    }
    if (externalStoreIdInput) {
      externalStoreIdInput.value =
        existing?.externalStoreId || activeStore.platformIds?.[activePlatform] || "";
    }
    if (statusSelect) statusSelect.value = existing?.status || "active";
    if (notesInput) notesInput.value = existing?.notes || "";
    if (titleElement) titleElement.textContent = existing ? "编辑平台绑定" : "添加平台绑定";
  };
  storeSelect?.addEventListener("change", () => {
    activeStoreId = storeSelect.value;
    refreshBindingFields();
  });
  platformSelect?.addEventListener("change", () => {
    activePlatform = platformSelect.value as TakeoutPlatformId;
    refreshBindingFields();
  });
  dialog.querySelectorAll("[data-close-binding]").forEach((button) => {
    button.addEventListener("click", () => dialog.close());
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.querySelector<HTMLFormElement>("#platform-binding-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const form = new FormData(formElement);
    const targetStoreId = storeId || activeStoreId;
    const targetPlatform = platform || activePlatform;
    const submit = formElement.querySelector<HTMLButtonElement>("button[type=submit]");
    if (submit) submit.disabled = true;
    try {
      const account = await saveTakeoutAccount({
        id: existing?.id,
        storeId: targetStoreId,
        platform: targetPlatform,
        accountName: String(form.get("accountName") || "").trim(),
        externalStoreId: String(form.get("externalStoreId") || "").trim(),
        status: String(form.get("status") || "active") === "inactive" ? "inactive" : "active",
        notes: String(form.get("notes") || "").trim()
      });
      state.takeoutAccounts = [
        account,
        ...state.takeoutAccounts.filter((item) => item.id !== account.id)
      ];
      state.dashboard = await getDashboard().catch(() => state.dashboard);
      dialog.close();
      toast(existing ? "平台绑定已更新" : "平台绑定已添加");
      if (state.page === "stores") renderPage();
      refreshIcons();
    } catch (error) {
      toast(error instanceof Error ? error.message : "保存失败", "error");
      if (submit) submit.disabled = false;
    }
  });
  dialog.addEventListener("close", () => dialog.remove());
}

function wireStores(): void {
  const search = document.querySelector<HTMLInputElement>("#store-search");
  const status = document.querySelector<HTMLSelectElement>("#status-filter");
  const owner = document.querySelector<HTMLSelectElement>("#owner-filter");
  const filter = (): void => {
    const query = (search?.value || "").trim().toLowerCase();
    const statusValue = status?.value || "";
    const ownerValue = owner?.value || "";
    document.querySelectorAll<HTMLTableRowElement>("[data-store-row]").forEach((row) => {
      const matchesQuery = `${row.textContent || ""} ${row.dataset.city || ""}`.toLowerCase().includes(query);
      const matchesStatus = !statusValue || row.dataset.status === statusValue;
      const matchesOwner = !ownerValue || row.dataset.owner === ownerValue;
      row.hidden = !matchesQuery || !matchesStatus || !matchesOwner;
    });
  };
  search?.addEventListener("input", filter);
  status?.addEventListener("change", filter);
  owner?.addEventListener("change", filter);
  document.querySelector("#reset-store-filters")?.addEventListener("click", () => {
    if (search) search.value = "";
    if (status) status.value = "";
    if (owner) owner.value = "";
    filter();
  });
  document.querySelector("#add-platform-binding")?.addEventListener("click", () => {
    openPlatformBindingForm();
  });
  document.querySelectorAll<HTMLButtonElement>("[data-manage-platforms]").forEach((button) => {
    button.addEventListener("click", () => openStorePlatformDialog(button.dataset.managePlatforms || ""));
  });
}

function wireData(): void {
  void listMetricRecords().then((metrics) => {
    state.metrics = metrics;
    if (state.page === "data") {
      renderPage();
      refreshIcons();
    }
  }).catch(() => {});
  document.querySelectorAll<HTMLButtonElement>("[data-template-platform]").forEach((button) => {
    button.addEventListener("click", () => {
      const platform = button.dataset.templatePlatform as TakeoutPlatformId;
      downloadTextFile(
        `${platformName(platform)}经营报表模板.csv`,
        platformReportTemplate(platform)
      );
    });
  });
  const dropZone = document.querySelector<HTMLLabelElement>("#file-drop");
  const fileInput = dropZone?.querySelector<HTMLInputElement>("input[type=file]");
  dropZone?.addEventListener("click", (event) => {
    if (event.target !== fileInput) fileInput?.click();
  });
  dropZone?.addEventListener("dragover", (event) => {
    event.preventDefault();
    dropZone.classList.add("dragging");
  });
  dropZone?.addEventListener("dragleave", () => dropZone.classList.remove("dragging"));
  dropZone?.addEventListener("drop", (event) => {
    event.preventDefault();
    dropZone.classList.remove("dragging");
    if (fileInput && event.dataTransfer?.files[0]) fileInput.files = event.dataTransfer.files;
    updateDropZone(dropZone, fileInput?.files?.[0]);
  });
  fileInput?.addEventListener("change", () => updateDropZone(dropZone, fileInput.files?.[0]));
  document.querySelector<HTMLFormElement>("#import-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const file = fileInput?.files?.[0];
    if (!file) return toast("请先选择 CSV 或 XLSX 文件", "error");
    if (file.size > 10 * 1024 * 1024) return toast("文件不能超过 10 MB", "error");
    const extension = file.name.toLowerCase().endsWith(".xlsx") ? "xlsx" : "csv";
    const content = extension === "xlsx" ? await fileToBase64(file) : await file.text();
    const output = document.querySelector("#import-result");
    if (output) output.innerHTML = loadingTemplate("正在校验报表");
    try {
      const result = await importMetricFile(content, extension);
      if (output) {
        output.innerHTML = `
          <div class="import-summary">
            <strong>校验完成</strong>
            <span>有效 ${result.totals.valid} 行，问题 ${result.totals.invalid} 行</span>
            <button class="primary-button" id="commit-import" data-import-id="${escapeHtml(result.importId)}">${icon("database-zap")} 确认入库</button>
          </div>
          <div class="issue-list">${result.issues.slice(0, 12).map((item) => `<span>第 ${item.row} 行 · ${escapeHtml(item.field)}：${escapeHtml(item.message)}</span>`).join("")}</div>
        `;
        document.querySelector("#commit-import")?.addEventListener("click", async (clickEvent) => {
          const button = clickEvent.currentTarget as HTMLButtonElement;
          button.disabled = true;
          try {
            await commitMetricImport(String(button.dataset.importId));
            toast("经营数据已入库");
            await loadWorkspace(false);
            renderPage();
          } catch (error) {
            toast(error instanceof Error ? error.message : "入库失败", "error");
            button.disabled = false;
          }
        });
      }
      refreshIcons();
    } catch (error) {
      if (output) output.innerHTML = `<div class="form-error">${escapeHtml(error instanceof Error ? error.message : "校验失败")}</div>`;
    }
  });
  document.querySelector<HTMLFormElement>("#metric-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const form = new FormData(formElement);
    try {
      await callApi("metric.save", Object.fromEntries(form.entries()));
      toast("指标已保存");
      formElement.reset();
      await loadWorkspace(false);
    } catch (error) {
      toast(error instanceof Error ? error.message : "保存失败", "error");
    }
  });
}

function updateDropZone(zone: HTMLLabelElement | null | undefined, file?: File): void {
  if (!zone) return;
  const strong = zone.querySelector("strong");
  const span = zone.querySelector("span");
  if (strong) strong.textContent = file?.name || "选择或拖入平台报表";
  if (span) span.textContent = file ? `${(file.size / 1024).toFixed(1)} KB` : "支持 .csv、.xlsx，单文件不超过 10 MB";
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function importMetricFile(
  content: string,
  extension: "csv" | "xlsx"
): Promise<{
  importId: string;
  totals: { rows: number; valid: number; invalid: number };
  issues: Array<{ row: number; field: string; message: string }>;
}> {
  return callApi("metric.importFile", extension === "xlsx" ? { contentBase64: content, extension } : { contentText: content, extension });
}

async function commitMetricImport(importId: string): Promise<void> {
  await callApi("metric.importCommit", { importId });
}

function wireContent(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-content-task]").forEach((button) => {
    button.addEventListener("click", async () => {
      const taskType = button.dataset.contentTask || "XHS_POST";
      const title = button.querySelector("strong")?.textContent || taskType;
      const output = document.querySelector<HTMLDivElement>("#content-task-output");
      if (output) output.innerHTML = loadingTemplate("正在创建内容任务");
      try {
        const task = await createTask({
          title,
          taskType,
          kind: taskType === "VIDEO_SCRIPT" ? "analysis" : "analysis",
          riskLevel: "low",
          status: "approved",
          priority: "P2",
          detail: "基于当前门店真实资料生成，不得编造缺失数据，结果仅供人工复核。"
        });
        if (output) output.innerHTML = taskCard(task);
        if (output) void pollInlineTask(task.id, output, "内容任务");
        refreshIcons();
        toast("内容任务已创建");
      } catch (error) {
        if (output) output.innerHTML = `<div class="form-error">${escapeHtml(error instanceof Error ? error.message : "创建失败")}</div>`;
      }
    });
  });
}

async function pollInlineTask(
  taskId: string,
  container: HTMLElement,
  label: string,
  attempt = 0
): Promise<void> {
  if (attempt >= 200) return;
  await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 1500 : 3000));
  try {
    const task = await getTask(taskId);
    state.tasks = state.tasks.map((item) => (item.id === task.id ? task : item));
    if (document.contains(container)) {
      container.innerHTML = `${taskCard(task)}${taskResultPreview(task)}`;
      wireTaskResultButtons(container);
      refreshIcons();
    }
    if (task.status === "succeeded") {
      toast(`${label}已完成`);
      return;
    }
    if (task.status === "failed" || task.status === "cancelled") {
      toast(task.errorMessage || `${label}未完成`, "error");
      return;
    }
    void pollInlineTask(taskId, container, label, attempt + 1);
  } catch {
    void pollInlineTask(taskId, container, label, attempt + 1);
  }
}

function wireMedia(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-upload-media]").forEach((button) => {
    button.addEventListener("click", () => {
      const kind = button.dataset.uploadMedia;
      document.querySelector<HTMLInputElement>(kind === "video" ? "#media-video-file" : "#media-image-file")?.click();
    });
  });
  document.querySelectorAll<HTMLInputElement>("#media-image-file, #media-video-file").forEach((input) => {
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) return;
      const output = document.querySelector("#media-task-output");
      if (output) output.innerHTML = loadingTemplate("正在上传文件");
      try {
        const folder = input.id === "media-video-file" ? "videos" : "images";
        const stored = await uploadFile(file, folder);
        const registeredAsset = await registerContentAsset({
          fileId: stored.fileId,
          type: folder === "videos" ? "video" : "image",
          metadata: { filename: file.name }
        });
        const taskKind = folder === "videos" ? "media" : "analysis";
        const task = await createTask({
          title: folder === "videos" ? "视频剪辑任务" : "图片优化任务",
          taskType: folder === "videos" ? "VIDEO_RENDER" : "IMAGE_ADVICE",
          kind: taskKind,
          riskLevel: "low",
          status: "approved",
          priority: "P2",
          detail: `已上传文件 ${file.name}`,
          payload: { fileId: stored.fileId, filename: file.name }
        });
        state.assets = [registeredAsset, ...state.assets];
        state.tasks = [task, ...state.tasks];
        renderPage();
        const refreshedOutput = document.querySelector<HTMLDivElement>("#media-task-output");
        if (refreshedOutput) refreshedOutput.innerHTML = taskCard(task);
        if (refreshedOutput) void pollInlineTask(task.id, refreshedOutput, "媒体任务");
        refreshIcons();
        toast("文件已上传并创建任务");
      } catch (error) {
        if (output) output.innerHTML = `<div class="form-error">${escapeHtml(error instanceof Error ? error.message : "上传失败")}</div>`;
      }
    });
  });
}

async function uploadFile(file: File, folder: "images" | "videos"): Promise<{ fileId: string; url?: string }> {
  const form = new FormData();
  form.append("folder", folder);
  form.append("file", file);
  form.append("session_token", currentApiToken());
  form.append("csrf_token", currentCsrfToken());
  const response = await fetch("/v1/upload", {
    method: "POST",
    credentials: "same-origin",
    headers: { "x-csrf-token": currentCsrfToken() },
    body: form
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.code !== 0) {
    throw new Error(body.message || "上传失败");
  }
  return body.data;
}

function wireTasks(): void {
  document.querySelector("#new-task-button")?.addEventListener("click", () => openNewTaskDialog());
  wireTaskResultButtons();
  document.querySelector("#task-filter")?.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-status]");
    if (!button) return;
    document.querySelectorAll("#task-filter button").forEach((item) => item.classList.toggle("active", item === button));
    const status = button.dataset.status || "";
    document.querySelectorAll<HTMLElement>("[data-task-id]").forEach((card) => {
      card.hidden = Boolean(status) && card.dataset.taskStatus !== status;
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-task-transition]").forEach((button) => {
    button.addEventListener("click", async () => {
      const card = button.closest<HTMLElement>("[data-task-id]");
      if (!card) return;
      button.disabled = true;
      try {
        await transitionTask(card.dataset.taskId || "", button.dataset.taskTransition as OpsTask["status"]);
        state.tasks = await listTasks();
        toast(
          button.dataset.taskTransition === "approved"
            ? "任务已批准"
            : button.dataset.taskTransition === "queued"
              ? "任务已重新排队"
              : "任务已取消"
        );
        renderPage();
        refreshIcons();
      } catch (error) {
        toast(error instanceof Error ? error.message : "操作失败", "error");
        button.disabled = false;
      }
    });
  });
}

function wireTaskResultButtons(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLButtonElement>("[data-task-result]").forEach((button) => {
    if (button.dataset.resultBound === "true") return;
    button.dataset.resultBound = "true";
    button.addEventListener("click", () => {
      const taskId = button.closest<HTMLElement>("[data-task-id]")?.dataset.taskId;
      const task = state.tasks.find((item) => item.id === taskId);
      if (task) openTaskResult(task);
    });
  });
}

function openNewTaskDialog(): void {
  const dialog = document.createElement("dialog");
  dialog.className = "modal";
  dialog.innerHTML = `
    <form method="dialog" class="modal-card" id="task-dialog-form">
      <div class="panel-head"><div><h2>新建任务</h2><span>高风险写操作将进入老板审批</span></div><button class="icon-button" type="button" data-close-task-dialog aria-label="关闭">${icon("x")}</button></div>
      <div class="form-grid">
        <label><span>标题</span><input name="title" required /></label>
        <label><span>类型</span><select name="kind"><option value="analysis">经营分析</option><option value="read">数据读取</option><option value="write">平台写操作</option><option value="media">媒体处理</option></select></label>
        <label><span>风险</span><select name="riskLevel"><option value="low">低风险</option><option value="medium">中风险</option><option value="high">高风险</option></select></label>
        <label><span>优先级</span><select name="priority"><option value="P2">P2</option><option value="P1">P1</option><option value="P0">P0</option></select></label>
        <label><span>门店</span><select name="storeId"><option value="">全组织</option>${(state.dashboard?.stores || []).map((store) => `<option value="${escapeHtml(store.id)}">${escapeHtml(store.name)}</option>`).join("")}</select></label>
        <label><span>平台</span><select name="platform"><option value="">不指定</option><option value="meituan">美团外卖</option><option value="taobao_flash">淘宝闪购</option><option value="jingdong">京东秒送</option></select></label>
      </div>
      <label><span>执行说明</span><textarea name="detail" rows="4" required></textarea></label>
      <div class="modal-actions"><button class="secondary-button" type="button" data-close-task-dialog>取消</button><button class="primary-button" id="save-new-task" type="submit">${icon("plus")} 创建任务</button></div>
    </form>
  `;
  document.body.append(dialog);
  refreshIcons();
  dialog.showModal();
  dialog.querySelectorAll<HTMLButtonElement>("[data-close-task-dialog]").forEach((button) => {
    button.addEventListener("click", () => dialog.close());
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener("close", () => dialog.remove());
  dialog.querySelector<HTMLFormElement>("#task-dialog-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const form = new FormData(formElement);
    const kind = String(form.get("kind") || "analysis") as TaskKind;
    const riskLevel = String(form.get("riskLevel") || "low") as TaskRiskLevel;
    try {
      await createTask({
        title: String(form.get("title") || ""),
        taskType: kind === "write" ? "平台操作" : kind === "media" ? "媒体任务" : "经营任务",
        detail: String(form.get("detail") || ""),
        kind,
        riskLevel,
        priority: String(form.get("priority") || "P2") as OpsTask["priority"],
        storeId: String(form.get("storeId") || "") || undefined,
        platform: (String(form.get("platform") || "") || undefined) as OpsTask["platform"],
        status: kind === "write" || riskLevel === "high" ? "pending_approval" : "approved"
      });
      state.tasks = await listTasks();
      dialog.close();
      dialog.remove();
      toast("任务已创建");
      renderPage();
      refreshIcons();
    } catch (error) {
      toast(error instanceof Error ? error.message : "创建失败", "error");
    }
  });
  dialog.addEventListener("close", () => dialog.remove());
}

function wireDevices(): void {
  document.querySelector("#new-pair-code")?.addEventListener("click", async () => {
    try {
      const code = await createPairingCode();
      state.pairingCodes = [
        code,
        ...state.pairingCodes.filter((item) => new Date(item.expiresAt).getTime() > Date.now())
      ];
      renderPage();
      refreshIcons();
    } catch (error) {
      toast(error instanceof Error ? error.message : "生成失败", "error");
    }
  });
  document.querySelector("#refresh-devices")?.addEventListener("click", async () => {
    state.devices = await listDevices();
    renderPage();
    refreshIcons();
  });
  document.querySelectorAll<HTMLButtonElement>("[data-revoke-device]").forEach((button) => {
    button.addEventListener("click", async () => {
      if (!window.confirm("撤销后该设备不能再领取任务，确定继续？")) return;
      try {
        await revokeDevice(button.dataset.revokeDevice || "");
        state.devices = await listDevices();
        renderPage();
        refreshIcons();
        toast("执行端已撤销");
      } catch (error) {
        toast(error instanceof Error ? error.message : "撤销失败", "error");
      }
    });
  });
}

function wireSubscription(): void {
  document.querySelector<HTMLFormElement>("#redeem-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const code = String(new FormData(formElement).get("code") || "");
    try {
      await callApi("subscription.redeem", { code });
      toast("激活码兑换成功");
      state.user = await getSession();
      renderShell();
    } catch (error) {
      toast(error instanceof Error ? error.message : "兑换失败", "error");
    }
  });
}

function wireSettings(): void {
  document.querySelector<HTMLFormElement>("#password-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget as HTMLFormElement;
    const form = new FormData(formElement);
    const password = String(form.get("password") || "");
    if (password !== String(form.get("confirmPassword") || "")) return toast("两次输入的密码不一致", "error");
    try {
      await changePassword(password);
      toast("密码已更新");
      formElement.reset();
    } catch (error) {
      toast(error instanceof Error ? error.message : "修改失败", "error");
    }
  });
}

async function loadWorkspace(showLoading = true): Promise<void> {
  if (showLoading && app) app.innerHTML = loadingTemplate("正在加载老板工作台");
  const [
    dashboard,
    metrics,
    tasks,
    devices,
    assets,
    integrations,
    takeoutAccounts,
    takeoutPlans,
    promotionPolicies
  ] = await Promise.all([
    getDashboard(),
    listMetricRecords().catch(() => []),
    listTasks(),
    listDevices().catch(() => []),
    listContentAssets().catch(() => []),
    listTakeoutIntegrations().catch(() => []),
    listTakeoutAccounts().catch(() => []),
    listTakeoutPlans().catch(() => []),
    listPromotionPolicies().catch(() => [])
  ]);
  state.dashboard = dashboard;
  state.metrics = metrics;
  state.tasks = tasks;
  state.devices = devices;
  state.assets = assets;
  state.integrations = integrations;
  state.takeoutAccounts = takeoutAccounts;
  state.takeoutPlans = takeoutPlans;
  state.promotionPolicies = promotionPolicies;
  const platformTaskId = sessionStorage.getItem("boss-platform-task-id");
  if (platformTaskId) {
    try {
      state.platformTask = await getTask(platformTaskId);
    } catch {
      sessionStorage.removeItem("boss-platform-task-id");
    }
  }
  if (!state.platformTask) {
    state.platformTask =
      tasks.find((task) =>
        ["PLATFORM_OPEN_ALL", "PLATFORM_COLLECT_ALL", "PLATFORM_PROMOTION_PLAN_ALL", "PLATFORM_EXPORT_ALL"].includes(task.taskType)
      ) || null;
  }
  renderShell();
  if (state.user?.mustChangePassword) {
    toast("首次登录请先修改初始密码", "error");
    navigate("settings");
  }
}

window.addEventListener("resize", () => state.chart?.resize());

async function bootstrap(): Promise<void> {
  try {
    state.user = await getSession();
    if (!state.user) {
      renderLogin();
      return;
    }
    await loadWorkspace();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      renderLogin();
      return;
    }
    renderLogin(error instanceof Error ? error.message : "无法连接云端服务");
  }
}

void bootstrap();
